import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ── Checklist de arranque ────────────────────────────────────────────────────
const QUICKSTART_KEY = 'lexmanager_quickstart_v1';

const quickstartByRole = {
  notario: [
    { id: 'notary-data',   icon: '🏛️', label: 'Datos de la notaría',             desc: 'Nombre, dirección, logotipo y tipo de cambio USD',   path: '/settings/notary',                time: '5 min' },
    { id: 'proc-types',    icon: '📋', label: 'Tipos de trámite',                 desc: 'Compraventa, Testamento, Poder Notarial, etc.',      path: '/settings/procedure-types',       time: '10 min' },
    { id: 'act-types',     icon: '🏷️', label: 'Tipos de actividad',              desc: 'Llamada, Visita, Gestión, Notificación, etc.',       path: '/settings/activity-types',        time: '3 min' },
    { id: 'concepts',      icon: '💰', label: 'Conceptos y cuentas bancarias',    desc: 'Honorarios, impuestos, descuentos y datos de pago',  path: '/settings/budget-document-types', time: '10 min' },
    { id: 'users',         icon: '👤', label: 'Crear usuarios del equipo',        desc: 'Invita a tu staff y supervisores',                   path: '/settings/users',                 time: '5 min' },
    { id: 'first-client',  icon: '👥', label: 'Registrar tu primer cliente',      desc: 'Prueba el flujo completo: cliente → expediente',    path: '/clients/create',                 time: '5 min' },
  ],
  supervisor: [
    { id: 'case-files',    icon: '📁', label: 'Revisar expedientes activos',      desc: 'Conoce el estado general de todos los trámites',    path: '/case-files',          time: '5 min' },
    { id: 'cxc',           icon: '💳', label: 'Revisar cuentas por cobrar',       desc: 'Ve qué saldos están pendientes o vencidos',         path: '/accounts-receivable', time: '5 min' },
    { id: 'exec-dash',     icon: '📈', label: 'Explorar el Dashboard Ejecutivo',  desc: 'KPIs de ingresos, expedientes y carga del equipo',  path: '/notary-dashboard',    time: '5 min' },
  ],
  staff: [
    { id: 'first-client',  icon: '👥', label: 'Registrar tu primer cliente',      desc: 'Nombre, teléfono, correo y motivo de la visita',    path: '/clients/create', time: '3 min' },
    { id: 'first-case',    icon: '📁', label: 'Abrir un expediente',              desc: 'Vincula el cliente a su trámite y asígnatelo',      path: '/case-files',     time: '3 min' },
    { id: 'first-appt',    icon: '📅', label: 'Agendar tu primera cita',          desc: 'Programa una cita y vinculala al expediente',       path: '/agenda',         time: '3 min' },
  ],
};

const roleTitle = {
  notario:    '¡Bienvenido! Configura el sistema en ~20 minutos',
  supervisor: 'Estos son tus puntos de partida como Supervisor',
  staff:      'Empieza aquí — tus primeras 3 acciones en el sistema',
};

function getQuickstartDone() {
  try { return JSON.parse(localStorage.getItem(QUICKSTART_KEY) || '[]'); } catch { return []; }
}
function saveQuickstartDone(ids) {
  try { localStorage.setItem(QUICKSTART_KEY, JSON.stringify(ids)); } catch {}
}

const sections = [
  // ─── PASO 1: PRIMER ACCESO ───────────────────────────────────────────────
  {
    id: 'introduccion',
    icon: '📖',
    title: 'Introducción',
    step: 1,
    roles: ['staff', 'supervisor', 'notario'],
    content: [
      {
        subtitle: 'Glosario rápido — términos del sistema',
        text: 'Antes de empezar, estos son los 7 términos que usarás constantemente en Lex Manager. Familiarízate con ellos y el resto del manual tendrá mucho más sentido.',
        glossary: [
          { term: 'Cliente',               def: 'La persona (física o moral) que contrata los servicios del despacho. Un mismo cliente puede tener múltiples expedientes abiertos al mismo tiempo.' },
          { term: 'Expediente',            def: 'El contenedor de un trámite. Un expediente = un servicio notarial. La compraventa de una casa y el testamento de la misma persona son dos expedientes separados del mismo cliente.' },
          { term: 'Tipo de trámite',       def: 'La categoría del servicio notarial: Compraventa, Testamento, Poder Notarial, etc. Cada expediente pertenece a un tipo. El notario los configura antes de empezar.' },
          { term: 'Actividad',             def: 'Cualquier acción registrada en un expediente: una llamada, una visita, un avance. Las actividades forman el historial de seguimiento del trámite.' },
          { term: 'Checklist',             def: 'Lista de documentos requeridos para un expediente (actas, escrituras, identificaciones). Cada documento tiene estado Pendiente o Entregado.' },
          { term: 'Presupuesto',           def: 'El documento de cobro: honorarios, impuestos y descuentos. Se genera en PDF para el cliente y registra los pagos recibidos conforme se van abonando.' },
          { term: 'CxC (Cuentas por Cobrar)', def: 'Vista que centraliza todos los presupuestos con saldo pendiente de pago. Solo el notario y el supervisor tienen acceso.' },
        ],
        example: null,
      },
      {
        subtitle: '¿Qué es Lex Manager?',
        text: `Lex Manager es el sistema central de gestión del despacho notarial. Fue diseñado para que todo el equipo tenga visibilidad completa del estado de cada cliente y trámite en tiempo real, desde el primer contacto hasta el cierre del expediente y la generación del presupuesto. El sistema elimina la necesidad de usar hojas de cálculo, notas sueltas o comunicación informal para dar seguimiento a los trámites: todo queda registrado, fechado y asignado en un solo lugar.`,
        example: null,
      },
      {
        subtitle: '¿Cómo está organizado?',
        text: `El sistema se divide en módulos accesibles desde el menú lateral izquierdo. Cada módulo tiene un propósito específico y están interconectados entre sí. Un cliente puede tener uno o varios expedientes. Cada expediente puede tener actividades, documentos, notas y un presupuesto asociado. La agenda se integra con los expedientes para registrar automáticamente las citas como actividades del trámite.`,
        example: null,
      },
      {
        subtitle: 'Flujo general de trabajo',
        text: `El flujo típico de un trámite en Lex Manager sigue estos pasos: primero se registra al cliente con sus datos de contacto y el motivo por el que se acerca a la notaría. Luego se crea un expediente vinculado al cliente, donde se define el tipo de trámite y el responsable. A lo largo del trámite el equipo registra actividades (llamadas, visitas, avances), gestiona el checklist de documentos requeridos y sube archivos a Google Drive. Cuando el trámite tiene un costo, se genera un presupuesto con los conceptos correspondientes y se registran los pagos. Las citas con el cliente se agendan desde el módulo de Agenda y quedan vinculadas al expediente automáticamente.`,
        example: 'Ejemplo: Una pareja llega a la notaría para una compraventa de inmueble. El staff la registra como cliente, crea el expediente de tipo "Compraventa", solicita los documentos requeridos, genera el presupuesto con impuestos y honorarios, agenda la cita de firma y el notario valida todo desde el Dashboard.',
      },
    ],
  },

  // ─── PASO 2: CONFIGURACIÓN INICIAL ───────────────────────────────────────
  {
    id: 'configuracion',
    icon: '⚙️',
    title: 'Configuración inicial',
    step: 2,
    roles: ['notario'],
    content: [
      {
        subtitle: 'Datos de la notaría',
        text: `Lo primero que debes hacer al recibir tu sistema es ingresar los datos generales del despacho. Ve a Configuración → Datos de la Notaría y llena: nombre de la notaría, dirección, teléfono, logotipo y el tipo de cambio USD que se utilizará al generar presupuestos en dólares. Estos datos aparecen en el encabezado del PDF de los presupuestos. El logotipo debe ser PNG o JPG de máximo 2 MB.`,
        example: 'Ejemplo: Nombre: Notaría Pública No. 45 · Dirección: Av. Insurgentes Sur 1234, CDMX · Teléfono: 55 4321 0987 · Tipo de cambio: 17.50',
        callouts: [
          { type: 'tip', text: 'Puedes actualizar el tipo de cambio USD en cualquier momento. Los presupuestos ya generados no se modifican — el tipo de cambio queda fijo al momento de crearlo.' },
        ],
      },
      {
        subtitle: 'Categorías y tipos de documentos',
        text: `Antes de registrar expedientes debes configurar el catálogo de tipos de trámites. Ve a Configuración → Tipos de Trámite. Primero crea las categorías (Inmobiliario, Mercantil, Familiar) y dentro de cada categoría crea los tipos específicos (Compraventa, Poder Notarial, Testamento). Cada tipo tiene: nombre en español e inglés, serie para foliado automático (ej. "CV") y leyenda de pie de página para el PDF.`,
        example: 'Ejemplo: Categoría: Inmobiliario → Tipo: Compraventa · Serie: CV · Leyenda: "Los presentes honorarios no incluyen gastos adicionales de escrituración."',
        callouts: [
          { type: 'important', text: 'Configura los tipos de trámite ANTES de crear el primer expediente. Una vez que un tipo está en uso no deberías eliminarlo para preservar el historial.' },
        ],
      },
      {
        subtitle: 'Categorías de conceptos y conceptos de presupuesto',
        text: `Configura las categorías que estructuran el presupuesto (Honorarios, Impuestos, Derechos y Gastos) y dentro de cada una los conceptos con su nombre en español e inglés y su costo base. Los conceptos pueden marcarse para incluir IVA. También configura los conceptos informativos (sin costo, como "Propietario" o "Valor de operación") y los conceptos de descuento (como "Retención ISR"). Cada categoría puede asociarse a una cuenta bancaria del despacho.`,
        example: null,
      },
      {
        subtitle: 'Cuentas bancarias y logo del banco',
        text: `Registra las cuentas bancarias del despacho que aparecerán en los presupuestos. Para cada cuenta guarda: etiqueta en español e inglés, nombre del banco, beneficiario, número de cuenta, CLABE, SWIFT y sucursal. Sube el logo del banco (PNG o JPG, máx. 2 MB) — aparecerá en el PDF junto a los datos de pago. Para quitar un logo existente, edita la cuenta y usa el botón "Quitar logo".`,
        example: 'Ejemplo: Banco: BBVA · Cuenta: 0114 2156 3478 · CLABE: 012320001142156347 · Titular: Notaría 45 SC · Logo: logo_bbva.png.',
      },
    ],
  },

  // ─── PASO 3: GOOGLE DRIVE ────────────────────────────────────────────────
  {
    id: 'google-drive-setup',
    icon: '🔗',
    title: 'Configurar Google Drive',
    step: 3,
    roles: ['notario'],
    content: [
      {
        subtitle: 'Requisitos previos',
        text: `Para habilitar la integración con Google Drive necesitas una cuenta de Google y acceso a Google Cloud Console (console.cloud.google.com). La configuración se realiza una sola vez y permite que el sistema cree carpetas y guarde archivos en el Drive asociado a esa cuenta.`,
        example: null,
        callouts: [
          { type: 'tip', text: 'Google Drive es completamente opcional. Si no lo configuras el sistema sigue funcionando al 100% — solo no podrás subir archivos al checklist ni generar respaldos automáticos. Puedes configurarlo ahora o en cualquier momento después.' },
        ],
      },
      {
        subtitle: 'Paso 1 — Crear proyecto en Google Cloud Console',
        text: `Entra a console.cloud.google.com e inicia sesión. En la parte superior izquierda haz clic en el selector de proyectos y luego en "Nuevo Proyecto". Asigna el nombre "Lex Manager Notaría" y haz clic en "Crear". Asegúrate de tenerlo seleccionado antes de continuar.`,
        example: null,
      },
      {
        subtitle: 'Paso 2 — Habilitar la API de Google Drive',
        text: `Dentro del proyecto ve a "APIs y servicios" → "Biblioteca". Busca "Google Drive API", selecciónala y haz clic en "Habilitar". Espera unos segundos a que se active.`,
        example: null,
      },
      {
        subtitle: 'Paso 3 — Crear credenciales OAuth 2.0',
        text: `Ve a "APIs y servicios" → "Credenciales" → "Crear credenciales" → "ID de cliente de OAuth". Si el sistema pide configurar la pantalla de consentimiento primero, selecciona "Externo", llena el nombre de la aplicación y el correo de soporte, guarda y continúa. De regreso en Credenciales, selecciona tipo "Aplicación web", ponle el nombre "Lex Manager OAuth" y en URIs de redireccionamiento agrega la siguiente URL exactamente como aparece:`,
        code: { label: 'URI de redireccionamiento autorizado', value: 'https://tu-dominio.com/api/auth/google/callback' },
        example: 'En desarrollo local usa: http://localhost:8080/api/auth/google/callback',
      },
      {
        subtitle: 'Paso 4 — Guardar credenciales en el sistema',
        text: `Al crear las credenciales Google mostrará el Client ID y el Client Secret. Cópialos. Ve a tu sistema → Configuración → Configuración General → sección "Credenciales Google" y pega los valores. Guarda los cambios. Estas credenciales quedan almacenadas de forma segura en la base de datos del sistema. El Client ID tiene este formato:`,
        code: { label: 'Formato del Client ID', value: '123456789012-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx.apps.googleusercontent.com' },
        example: null,
      },
      {
        subtitle: 'Paso 5 — Conectar Drive desde el sistema',
        text: `Ve a Configuración → Configuración General → sección "Integraciones" y haz clic en "Conectar con Google Drive". El sistema te redirigirá a Google para autorizar el acceso. Una vez autorizado regresarás al sistema con el estado "Conectado". Si aparece error de acceso restringido, agrega tu correo como usuario de prueba en Google Cloud Console → "Pantalla de consentimiento de OAuth" → "Usuarios de prueba".`,
        example: null,
        callouts: [
          { type: 'important', text: 'Si el token expira (el sistema muestra error de Drive), ve a Configuración → Integraciones y usa el botón "Reconectar" para renovarlo sin perder la configuración.' },
        ],
      },
    ],
  },

  // ─── PASO 4: USUARIOS ────────────────────────────────────────────────────
  {
    id: 'usuarios',
    icon: '👤',
    title: 'Gestión de usuarios',
    step: 4,
    roles: ['notario'],
    content: [
      {
        subtitle: '¿Quién puede gestionar usuarios?',
        text: `Solo el notario puede crear, editar, desactivar y cambiar la contraseña de los usuarios del sistema. El módulo de Usuarios está en el menú lateral bajo "Configuración". Antes de invitar a tu equipo asegúrate de haber configurado los tipos de trámite y datos de la notaría (pasos 2 y 3) para que cuando inicien sesión ya encuentren el sistema listo.`,
        example: null,
      },
      {
        subtitle: 'Crear un usuario',
        text: `Ve a Configuración → Usuarios y haz clic en "Nuevo Usuario". Llena los campos: nombre completo, correo electrónico (será el usuario para iniciar sesión), contraseña temporal y rol. Selecciona el rol según las responsabilidades de la persona: Staff para el personal operativo, Supervisor para quien supervisa al equipo, o Notario para acceso completo. Haz clic en "Crear". El usuario podrá iniciar sesión de inmediato con el correo y la contraseña temporal que le compartas.`,
        code: { label: 'URL de acceso al sistema', value: window.location.origin + '/login' },
        example: 'Ejemplo: Nombre: Ana Torres · Correo: ana@notaria45.com · Contraseña temporal: Notaria2026! · Rol: Staff. Comparte estas credenciales con Ana y pídele que cambie su contraseña en el primer inicio de sesión.',
        callouts: [
          { type: 'tip', text: 'Comparte la URL del sistema junto con las credenciales para que el usuario pueda acceder directamente sin tener que buscarla.' },
        ],
      },
      {
        subtitle: 'Roles disponibles y sus diferencias',
        text: `Hay tres roles en el sistema. Staff: ve y gestiona únicamente los clientes y expedientes que le están asignados directamente; no tiene acceso a configuración, cuentas por cobrar ni dashboard ejecutivo. Supervisor: tiene visibilidad completa de todos los clientes y expedientes del despacho, puede ver notas privadas y tiene acceso a cuentas por cobrar y dashboard ejecutivo, pero no puede configurar el sistema. Notario: acceso completo sin restricciones, puede validar pagos, ver auditoría y acceder a toda la configuración.`,
        example: 'Ejemplo de equipo típico: 1 Notario (dueño del sistema) + 1 Supervisor (jefe de staff) + 2-4 Staff (personal operativo que atiende clientes).',
      },
      {
        subtitle: 'Cambiar la contraseña de un usuario',
        text: `Si un miembro del equipo olvida su contraseña o necesitas restablecerla, ve a Configuración → Usuarios, busca al usuario y haz clic en el ícono de edición. En el formulario de edición hay un campo "Nueva contraseña" — ingrésala y guarda. La nueva contraseña aplica de inmediato. El usuario no recibe notificación automática, así que debes compartirle la nueva contraseña de forma directa.`,
        example: 'Ejemplo: Ana Torres olvidó su contraseña. El notario entra a Usuarios, abre el perfil de Ana, escribe la nueva contraseña "Notaria2026#" y guarda. Le avisa a Ana por WhatsApp con la nueva contraseña.',
      },
      {
        subtitle: 'Desactivar y reactivar un usuario',
        text: `Cuando un empleado deja el despacho, desactívalo en lugar de eliminarlo para conservar el historial de todo lo que hizo. Ve a Configuración → Usuarios, abre el perfil del usuario y desactiva el toggle "Usuario activo". El usuario pierde acceso al sistema de inmediato y todas sus sesiones activas se cierran. Sus expedientes y actividades siguen visibles asignados a su nombre. Puedes reactivarlo en cualquier momento activando el toggle nuevamente.`,
        example: 'Ejemplo: Carlos López deja la notaría. El notario desactiva su usuario. Carlos ya no puede entrar al sistema pero todos sus expedientes y registros históricos permanecen intactos y visibles para el equipo.',
        callouts: [
          { type: 'warning', text: 'Al desactivar un usuario todas sus sesiones activas se cierran de inmediato. El usuario pierde acceso al sistema en ese momento.' },
        ],
      },
      {
        subtitle: 'Reasignar expedientes de un usuario desactivado',
        text: `Después de desactivar a un usuario, sus expedientes activos siguen asignados a él pero nadie los está atendiendo. Ve al módulo de Expedientes, filtra por el responsable desactivado y reasigna cada expediente al responsable correcto. Para reasignar, abre el expediente, edita el campo "Responsable" y guarda. El cambio queda registrado en el historial de auditoría del expediente.`,
        example: 'Ejemplo: Carlos tenía 6 expedientes activos. El notario los filtra, los abre uno por uno y los reasigna entre Ana Torres (4 expedientes) y el nuevo empleado Luis Pérez (2 expedientes).',
      },
    ],
  },

  // ─── PASO 5: CLIENTES ────────────────────────────────────────────────────
  {
    id: 'clientes',
    icon: '👥',
    title: 'Clientes',
    step: 5,
    roles: ['staff', 'supervisor', 'notario'],
    content: [
      {
        subtitle: 'Registrar un cliente',
        text: `Para registrar un nuevo cliente, ve al módulo "Clientes" en el menú lateral y haz clic en el botón "Nuevo Cliente". Deberás ingresar los siguientes datos: nombre completo, teléfono, correo electrónico, dirección y el motivo por el cual se acerca a la notaría. El campo de motivo es importante porque permite entender desde el inicio qué tipo de trámite necesita el cliente y facilita la creación del expediente correspondiente.`,
        example: 'Ejemplo: Nombre: Juan García López · Teléfono: 442 123 4567 · Correo: juan@email.com · Dirección: Av. Constituyentes 150, Querétaro · Motivo: Compraventa de casa habitación en Juriquilla.',
      },
      {
        subtitle: 'Editar información del cliente',
        text: `Para editar los datos de un cliente existente, búscalo en la lista de clientes, entra a su perfil haciendo clic en su nombre y usa el botón de edición. Puedes actualizar cualquier campo en cualquier momento. Los cambios quedan guardados inmediatamente y se reflejan en todos los módulos donde aparece ese cliente.`,
        example: null,
      },
      {
        subtitle: 'Buscar un cliente',
        text: `Puedes buscar clientes desde la barra de búsqueda global ubicada en la parte superior del sistema. La búsqueda encuentra clientes por nombre en tiempo real conforme escribes. También puedes filtrar y ordenar desde la lista del módulo de Clientes usando los filtros disponibles. El staff solo verá los clientes que tiene asignados; supervisores y notario ven todos los clientes del despacho.`,
        example: 'Ejemplo: Si buscas "García", el sistema mostrará todos los clientes cuyo nombre contenga esa palabra, junto con sus expedientes activos asociados.',
      },
      {
        subtitle: 'Visibilidad según rol',
        text: `El staff únicamente puede ver y gestionar los clientes que le han sido asignados directamente. El supervisor tiene visibilidad de todos los clientes del despacho sin excepción. El notario tiene acceso completo a todos los clientes y puede reasignar responsables desde el perfil del expediente.`,
        example: null,
      },
    ],
  },

  // ─── PASO 6: EXPEDIENTES ─────────────────────────────────────────────────
  {
    id: 'expedientes',
    icon: '📁',
    title: 'Expedientes',
    step: 6,
    roles: ['staff', 'supervisor', 'notario'],
    content: [
      {
        subtitle: 'Crear un expediente',
        text: `Un expediente representa el trámite formal de un cliente. Para crearlo puedes hacerlo desde el módulo "Expedientes" haciendo clic en "Nuevo Expediente", o directamente desde el perfil de un cliente con el botón "Crear expediente". Al crear el expediente debes: asociarlo a un cliente existente, escribir el título del expediente, seleccionar el tipo de trámite (procedimiento notarial), asignar un responsable del equipo y definir el estado inicial. Una vez creado, el expediente tiene su propio espacio con pestañas para documentos, actividades, presupuestos, notas e historial de cambios.`,
        example: 'Ejemplo: Cliente: Juan García López · Título: Compraventa Calle Hidalgo 45 · Tipo: Compraventa · Responsable: Ana Torres · Estado: En proceso.',
      },
      {
        subtitle: 'Registrar actividades',
        text: `Dentro de cada expediente existe un registro de actividades donde se documenta todo lo que ocurre con el trámite. Para agregar una actividad ve a la pestaña "Actividades" y haz clic en "Nueva". Debes seleccionar el tipo de actividad (llamada, visita, gestión, notificación, etc.), escribir el título y la descripción detallada de lo que ocurrió, indicar el estado (pendiente, en progreso, completado) y opcionalmente registrar con quién se entrevistó. Cada actividad queda registrada con la fecha, hora y el nombre del usuario que la creó.`,
        example: 'Ejemplo: Tipo: Llamada · Título: Llamada para solicitar documentos · Descripción: Se llamó al cliente para solicitar escritura anterior, identificación oficial y comprobante de domicilio. El cliente indicó que los entregará el viernes. · Estado: Completado.',
      },
      {
        subtitle: 'Tiempo de seguimiento y alertas',
        text: `El sistema muestra automáticamente el tiempo transcurrido desde que se abrió el expediente y desde la última actividad registrada. Si un expediente no ha tenido actividad en un periodo prolongado (configurable por el notario), aparece marcado con una alerta naranja tanto en la lista de expedientes como en el Dashboard. Esta alerta es la señal para que el equipo tome acción antes de que el trámite se detenga.`,
        example: 'Ejemplo: Un expediente abierto hace 45 días sin actividad en los últimos 10 días aparecerá con alerta ⚠️ en el Dashboard indicando que requiere seguimiento urgente.',
      },
      {
        subtitle: 'Checklist de documentos requeridos',
        text: `Cada expediente tiene un checklist de documentos en la pestaña "Documentos". Puedes agregar documentos individualmente indicando el nombre, si es requerido u opcional, quién lo entregó y quién lo recibió. Conforme el cliente va entregando documentos, cambias su estado de "Pendiente" a "Entregado". El sistema muestra el avance en formato X/Y en el Dashboard. Si Google Drive está conectado, puedes subir el archivo digital directamente desde el checklist.`,
        example: 'Ejemplo: ✅ Identificación oficial vendedor · ✅ Escritura anterior · ⏳ Comprobante de domicilio · ⏳ Avalúo del inmueble.',
      },
      {
        subtitle: 'Notas internas del expediente',
        text: `La pestaña "Notas" permite agregar anotaciones rápidas al expediente sin necesidad de crear una actividad formal. El supervisor y el notario pueden marcar notas como "privadas" — el staff no tiene acceso a las notas privadas. Las notas se muestran con el nombre del autor, la fecha y hora, y pueden editarse o eliminarse por quien las creó o por el notario.`,
        example: 'Ejemplo de nota privada: "El cliente mencionó que su apoderado legal revisará la escritura antes de firmar. Coordinar con el notario antes de agendar la firma."',
      },
      {
        subtitle: 'Historial de cambios',
        text: `La pestaña "Historial" (visible solo para el notario) muestra un registro completo de todos los cambios realizados en el expediente: cambios de estado, modificaciones de campos, documentos agregados, archivos subidos a Drive y más. Cada entrada muestra el valor anterior, el valor nuevo, quién hizo el cambio y cuándo. Esto permite al notario tener trazabilidad completa de todo lo que ocurrió.`,
        example: null,
      },
      {
        subtitle: 'Documentos en Google Drive',
        text: `Si el sistema tiene integración con Google Drive activa, cada expediente puede tener una carpeta en Drive. Para crearla, ve al panel lateral del expediente y haz clic en "Crear carpeta en Drive". A partir de ese momento puedes subir archivos directamente desde el checklist de documentos — el archivo se guarda en Drive y queda vinculado al documento en el sistema.`,
        example: null,
        callouts: [
          { type: 'important', text: 'Debes crear primero la carpeta en Drive desde el panel lateral del expediente. Sin ella, el botón de subir archivos en el checklist estará deshabilitado.' },
        ],
      },
      {
        subtitle: 'Agendar cita desde el expediente',
        text: `Desde cualquier expediente puedes programar una cita directamente sin necesidad de ir al módulo de Agenda. En el encabezado del expediente hay un botón "📅 Agendar cita" — al hacerlo el modal se abre con el cliente y el expediente ya pre-llenados automáticamente. Solo necesitas indicar el título, tipo, responsable, fecha y hora.`,
        example: 'Ejemplo: Estás revisando el expediente de compraventa de Juan García y todos los documentos están completos. Sin salir del expediente haces clic en "Agendar cita", el modal se abre con Juan García ya vinculado. Solo escribes "Firma de escritura" y seleccionas fecha y hora.',
      },
    ],
  },

  // ─── PASO 7: AGENDA ──────────────────────────────────────────────────────
  {
    id: 'agenda',
    icon: '📅',
    title: 'Agenda',
    step: 7,
    roles: ['staff', 'supervisor', 'notario'],
    content: [
      {
        subtitle: '¿Para qué sirve la Agenda?',
        text: `La Agenda permite programar, dar seguimiento y registrar todas las citas del despacho en un calendario visual. Cada cita puede vincularse a un cliente y a un expediente específico, lo que hace que cuando se completa una cita, la información pueda registrarse automáticamente como actividad en el expediente — eliminando la doble captura de información. El notario y el supervisor pueden ver las citas de todo el equipo; el staff ve únicamente las citas que le están asignadas.`,
        example: null,
      },
      {
        subtitle: 'Vistas del calendario',
        text: `La Agenda tiene tres vistas que puedes alternar con los botones en la parte superior derecha. La vista Semana muestra los 7 días con las citas en franjas horarias de 8:00 a 18:00 — al hacer clic en el encabezado de cualquier día cambia a la vista de ese día. La vista Día muestra con detalle todas las citas de un día específico — ideal para el trabajo diario. La vista Lista muestra todas las citas de la semana agrupadas por día en formato de lista, útil para revisiones rápidas. Para navegar entre semanas usa los botones ‹ › ; el botón "Hoy" regresa a la semana actual.`,
        example: null,
      },
      {
        subtitle: 'Crear una cita desde la Agenda',
        text: `Opción 1: haz clic en el botón "+ Nueva cita" en la parte superior — se abre el modal con todos los campos vacíos. Opción 2: haz clic directamente en cualquier franja horaria del calendario — el modal se abre con la fecha y hora de esa franja preseleccionada. En el modal indica: título, tipo (Firma, Consulta, Entrega, Seguimiento o Interno), ubicación, responsable, fecha y hora. Opcionalmente selecciona un cliente para vincular la cita a uno de sus expedientes.`,
        example: 'Ejemplo: Haces clic en el martes a las 10:00 AM. El modal se abre con esa hora preseleccionada. Llenas: "Consulta inicial compraventa" · Tipo: Consulta · Cliente: Juan García · Expediente: Compraventa Calle Hidalgo 45.',
      },
      {
        subtitle: 'Estados de una cita',
        text: `Cada cita tiene un estado representado por un color. Azul (Programada): estado inicial. Verde (Confirmada): el cliente confirmó asistencia. Gris (Realizada): la cita ocurrió. Rojo (Cancelada): no se llevará a cabo. Amarillo (Reprogramada): fue movida a otra fecha. Las citas canceladas y realizadas no desaparecen del calendario para mantener el registro histórico.`,
        example: null,
      },
      {
        subtitle: 'Confirmar, realizar y cancelar citas',
        text: `Para confirmar: abre el detalle de la cita (clic en ella en el calendario) y haz clic en "Confirmar". Para marcar como realizada: haz clic en "Marcar realizada" y agrega las notas de lo que ocurrió. Si la cita está vinculada a un expediente, el sistema preguntará si deseas registrar automáticamente una actividad en ese expediente — al aceptar, crea la actividad sin que tengas que hacerlo manualmente. Para cancelar: haz clic en "Cancelar cita" e indica el motivo.`,
        example: 'Ejemplo: Tras la firma, Ana abre la cita, hace clic en "Marcar realizada" y escribe: "Firma completada. Ambas partes firmaron sin incidencias." Acepta registrar actividad en el expediente — el sistema la crea automáticamente.',
      },
      {
        subtitle: 'Reprogramar una cita',
        text: `Si una cita necesita moverse, usa el botón "Reprogramar" en lugar de editar la fecha directamente. Al reprogramar, el sistema marca la cita original como "Reprogramada" (amarillo) y crea automáticamente una nueva cita con los mismos datos pero con la nueva fecha. Así siempre es posible rastrear cuántas veces se reprogramó y por qué.`,
        example: 'Ejemplo: La firma estaba para el lunes pero el vendedor tuvo una emergencia. Ana reprograma para el miércoles. La cita del lunes queda en amarillo con el motivo registrado y se crea la nueva cita del miércoles.',
      },
      {
        subtitle: 'Widget de citas en el Dashboard',
        text: `El Dashboard muestra un widget "Citas de hoy" con todas las citas del día actual en estado Programada o Confirmada, con su horario, cliente y responsable. Desde ahí puedes hacer clic en "Ver" para ir directamente al detalle de cualquier cita.`,
        example: 'Ejemplo: Al iniciar el día el notario ve en el Dashboard: 9:00 consulta, 11:00 entrega de documentos, 16:00 firma — con clientes y responsables.',
      },
      {
        subtitle: 'Estadísticas de agenda',
        text: `La Agenda tiene un módulo de estadísticas accesible desde el botón "📊 Estadísticas". Muestra: total de citas del mes, cuántas se realizaron, cancelaron y reprogramaron, tasa de completado, gráfica por día, desglose por tipo y tabla de citas por responsable. Usa los selectores de mes y año para cambiar el periodo.`,
        example: 'Ejemplo: El notario revisa abril: 18 citas realizadas, 3 canceladas, 2 reprogramadas, 75% de tasa de completado. Ana Torres atendió 12 citas (67%) y Carlos López 6 (33%).',
      },
    ],
  },

  // ─── PASO 8: PRESUPUESTOS ────────────────────────────────────────────────
  {
    id: 'presupuestos',
    icon: '💰',
    title: 'Presupuestos',
    step: 8,
    roles: ['staff', 'supervisor', 'notario'],
    content: [
      {
        subtitle: 'Crear un presupuesto',
        text: `Para crear un presupuesto ve al módulo "Presupuestos" y haz clic en "Nuevo Presupuesto", o hazlo directamente desde la pestaña "Presupuestos" dentro de un expediente. Al crearlo seleccionas el tipo de documento notarial y el sistema carga automáticamente la estructura de conceptos configurada para ese tipo. Puedes modificar los montos y agregar o eliminar conceptos según las particularidades del trámite.`,
        example: 'Ejemplo: Abres el expediente de compraventa de Juan García, vas a la pestaña Presupuestos y haces clic en "Nuevo Presupuesto". Seleccionas "Compraventa". El sistema carga: Honorarios $25,000, ISR $8,500, Derechos de registro $3,200, IVA $4,000. Ajustas el valor de la operación y guardas.',
      },
      {
        subtitle: 'Estados del presupuesto — flujo de vida',
        text: `Todo presupuesto pasa por cuatro estados. Borrador: recién creado, en edición. Puedes generar el PDF y enviárselo al cliente. Enviado: indica que el cliente ya lo recibió para revisión — el presupuesto aún no está comprometido. Aceptado: el cliente aprobó el presupuesto. A partir de este estado aparece en Cuentas por Cobrar y se pueden registrar pagos. Liquidado: el saldo llegó a $0 — el presupuesto desaparece de CxC automáticamente. El flujo es lineal pero puedes volver a "Borrador" si necesitas hacer ajustes significativos.`,
        example: 'Flujo típico: Borrador (generas y ajustas) → Enviado (mandas PDF al cliente) → Aceptado (el cliente aprueba y empieza a pagar) → Liquidado (saldo $0, trámite cobrado).',
        callouts: [
          { type: 'important', text: 'Si registras un pago y el presupuesto sigue en estado "Borrador" o "Enviado", ese pago NO aparece en el Dashboard Ejecutivo ni en Cuentas por Cobrar. Cambia el estado a "Aceptado" primero.' },
        ],
      },
      {
        subtitle: 'Tipos de conceptos',
        text: `El presupuesto maneja tres tipos de conceptos. Informativos: datos de referencia sin costo que aparecen en el PDF (nombre del propietario, valor de la operación). De costo: servicios e impuestos con monto; algunos pueden tener IVA incluido con desglose automático. De descuento: deducciones aplicables al total (retención ISR, importe INFONAVIT); se restan del total y se muestran diferenciados en el PDF.`,
        example: 'Ejemplo: [Informativo] Valor de operación: $1,850,000 · [Costo] Honorarios: $25,000 + IVA $4,000 · [Descuento] Retención ISR: -$8,500 · Total a pagar: $23,700.',
      },
      {
        subtitle: 'Registrar pagos y comprobantes',
        text: `Dentro del presupuesto puedes registrar los pagos que el cliente va realizando. Haz clic en "Registrar pago" e indica el monto, fecha, método de pago (transferencia, cheque, efectivo) y sube el comprobante. Si Drive está conectado, el comprobante se guarda automáticamente en la carpeta del expediente. El balance pendiente se actualiza solo. Solo el notario puede validar un pago registrado por el staff.`,
        example: 'Ejemplo: El cliente hace un anticipo del 50% ($11,850). El staff registra el pago y sube el comprobante. Sistema muestra: Pagado $11,850 · Saldo pendiente $11,850. El notario valida.',
        callouts: [
          { type: 'important', text: 'Un pago registrado por staff queda en estado "Pendiente" hasta que el notario lo valide. Solo los pagos validados se suman al total cobrado en el Dashboard Ejecutivo.' },
        ],
      },
      {
        subtitle: 'Generar PDF del presupuesto',
        text: `El presupuesto puede generarse como PDF en dos estilos. Clásico: diseño limpio en blanco y negro — ideal para archivo interno o impresión formal. Elegante: diseño en azul oscuro con detalles en dorado — ideal para presentar al cliente. Ambos incluyen: datos de la notaría, información del cliente, tabla de conceptos con subtotales, descuentos, total en número y letras, y tarjetas de cuentas bancarias con el logo del banco.`,
        example: 'Ejemplo: Para la reunión con el cliente usas el estilo Elegante. Para el archivo interno del despacho generas el mismo presupuesto en estilo Clásico.',
      },
      {
        subtitle: 'Presupuesto en inglés y en dólares',
        text: `El presupuesto puede generarse en inglés con montos en dólares. Para esto, cada tipo de documento y sus conceptos deben tener configurado su nombre en inglés desde Configuración. El sistema usa el tipo de cambio configurado en la notaría para convertir todos los montos al momento de generar el PDF.`,
        example: null,
      },
      {
        subtitle: 'Historial de versiones',
        text: `Cada presupuesto guarda un historial de versiones manual: haces los cambios que necesitas y cuando terminas haces clic en "Guardar versión" con una descripción de qué cambió. El sistema crea la versión 1 automáticamente al crear el presupuesto. Cuando hay cambios sin versionar, aparece un punto naranja en la pestaña "Versiones" y un banner amarillo con un botón directo para guardar.`,
        example: 'Ejemplo: Ana modifica los honorarios. Aparece el punto naranja. Termina sus ajustes, hace clic en "Guardar versión" y escribe: "Ajuste de honorarios por solicitud del notario." El indicador desaparece.',
      },
      {
        subtitle: 'Comparar y restaurar versiones',
        text: `Para comparar dos versiones, selecciona ambas con sus checkboxes y haz clic en "Comparar" — el panel muestra totales lado a lado y cambios en verde (nuevo), rojo (eliminado) y amarillo (modificado). El notario puede restaurar cualquier versión anterior haciendo clic en "↩ Restaurar": el historial es lineal y completo, las versiones anteriores nunca se eliminan.`,
        example: 'Ejemplo: El notario detecta un error en la versión 5. Restaura la versión 4. El presupuesto vuelve a tener los conceptos de v4 y se crea automáticamente la versión 6 con la descripción "Restauró versión #4".',
      },
    ],
  },

  // ─── PASO 9: CUENTAS POR COBRAR ──────────────────────────────────────────
  {
    id: 'cuentas-por-cobrar',
    icon: '💳',
    title: 'Cuentas por Cobrar',
    step: 9,
    roles: ['supervisor', 'notario'],
    content: [
      {
        subtitle: '¿Qué son las Cuentas por Cobrar?',
        text: `El módulo de Cuentas por Cobrar centraliza en una sola vista todos los presupuestos que tienen saldo pendiente de pago. En lugar de revisar cada presupuesto uno por uno, el notario y el supervisor pueden ver de inmediato cuánto se debe cobrar en total y qué presupuestos llevan más de 30 días sin liquidarse.`,
        example: null,
        callouts: [
          { type: 'important', text: 'Solo aparecen aquí los presupuestos con estado "Aceptado" y saldo mayor a $0. Un presupuesto en estado "Borrador" o "Enviado" no aparece en CxC aunque tenga conceptos definidos. Recuerda cambiar el estado a "Aceptado" cuando el cliente apruebe el presupuesto.' },
        ],
      },
      {
        subtitle: 'Tarjetas de resumen',
        text: `En la parte superior aparecen tres tarjetas. Amarilla: saldo total pendiente de todos los presupuestos con balance mayor a cero. Roja: saldo vencido — presupuestos "Aceptados" que llevan más de 30 días sin liquidarse completamente. Verde: total ya cobrado (suma de todos los pagos validados).`,
        example: 'Ejemplo: Saldo pendiente: $145,200 (8 presupuestos) · Vencido: $62,500 (3 presupuestos) · Total cobrado: $380,000.',
      },
      {
        subtitle: 'Tabla de saldos y filtros',
        text: `La tabla muestra todos los presupuestos con saldo pendiente, ordenados mostrando primero los vencidos y luego por monto. Cada fila tiene el folio, cliente, tipo, responsable, total, pagado, saldo pendiente, días abierto y estado. Los vencidos tienen fondo rosa y saldo en rojo. La columna "Días" usa colores: gris (<15 días), naranja (15-30 días) y rojo (>30 días). Usa los botones "Todos" y "Vencidos" para filtrar.`,
        example: null,
      },
      {
        subtitle: 'Alerta en el Dashboard principal',
        text: `El Dashboard muestra automáticamente una alerta cuando hay saldos pendientes. Roja si hay vencidos; amarilla si solo hay pendientes sin vencer. Muestra el total por cobrar y un botón directo al módulo de Cuentas por Cobrar.`,
        example: 'Ejemplo: El notario abre el sistema y ve: 🔴 3 presupuestos con saldo vencido — Total: $145,200. Hace clic y va directo al módulo.',
      },
    ],
  },

  // ─── PASO 10: DASHBOARD ──────────────────────────────────────────────────
  {
    id: 'dashboard',
    icon: '📊',
    title: 'Dashboard',
    step: 10,
    roles: ['staff', 'supervisor', 'notario'],
    content: [
      {
        subtitle: '¿Qué muestra el Dashboard?',
        text: `El Dashboard es la pantalla principal del sistema y muestra un resumen ejecutivo del estado general del despacho. Es lo primero que se ve al iniciar sesión. De un vistazo, sin necesidad de entrar a cada módulo, puedes saber cuántos expedientes hay activos, cuáles requieren atención urgente, cómo está distribuida la carga de trabajo y qué citas hay hoy.`,
        example: null,
      },
      {
        subtitle: 'Tarjetas de resumen',
        text: `En la parte superior aparecen cuatro tarjetas: Expedientes activos (total abiertos o en proceso), Sin actividad (expedientes sin movimiento en los días configurados — resaltada en naranja si hay alguno), Completados (expedientes cerrados en el periodo) y Clientes este mes (nuevos clientes en el mes actual).`,
        example: 'Ejemplo: Si ves "5" en la tarjeta "Sin actividad" resaltada en naranja, hay 5 expedientes que requieren atención inmediata.',
      },
      {
        subtitle: 'Alertas y gráficos',
        text: `Debajo de las tarjetas aparecen las alertas: naranja para expedientes sin actividad y roja/amarilla para cuentas por cobrar. El Dashboard también muestra un gráfico de barras con la distribución de expedientes por estado y una tabla de expedientes con más tiempo abiertos, ordenados de mayor a menor antigüedad.`,
        example: null,
      },
      {
        subtitle: 'Carga de trabajo por usuario',
        text: `La tabla de carga de trabajo muestra cuántos expedientes activos tiene cada persona, cuántos están sin actividad reciente y una barra visual de carga. Permite al notario y supervisor identificar si algún miembro del equipo tiene una carga excesiva.`,
        example: 'Ejemplo: Ana Torres tiene 12 expedientes activos y 3 con alerta. Puedes redistribuir la carga o hablar con ella para priorizar.',
      },
      {
        subtitle: 'Visibilidad según rol',
        text: `El staff ve únicamente la información de sus clientes y expedientes asignados: sus expedientes activos, sus alertas y sus citas. El supervisor y notario ven la información completa del despacho incluyendo carga de trabajo de cada usuario, métricas globales y alerta de cuentas por cobrar.`,
        example: null,
      },
    ],
  },

  // ─── PASO 11: DASHBOARD EJECUTIVO ────────────────────────────────────────
  {
    id: 'dashboard-ejecutivo',
    icon: '📈',
    title: 'Dashboard Ejecutivo',
    step: 11,
    roles: ['supervisor', 'notario'],
    content: [
      {
        subtitle: '¿Qué es el Dashboard Ejecutivo?',
        text: `El Dashboard Ejecutivo es una vista gerencial exclusiva para el notario y el supervisor. A diferencia del Dashboard general que muestra el estado operativo del día a día, el Dashboard Ejecutivo está diseñado para tomar decisiones de negocio: ver si los ingresos suben o bajan respecto al mes anterior, identificar qué tipos de trámite son más frecuentes y detectar quién tiene sobrecarga de trabajo.`,
        example: null,
      },
      {
        subtitle: 'KPIs: Cobrado, por cobrar, expedientes',
        text: `La primera tarjeta muestra el total cobrado en el mes actual con variación porcentual respecto al mes anterior (verde si subió, rojo si bajó) y un mini gráfico de barras de las últimas 8 semanas. La segunda tarjeta muestra el saldo pendiente total. Las tarjetas tercera y cuarta muestran expedientes activos y cerrados este mes, cada una con su tendencia de los últimos 6 meses.`,
        example: 'Ejemplo: Cobrado: $185,000 · ▲ 23.4% vs mes anterior. Expedientes cerrados: 6 · El gráfico muestra tendencia al alza desde enero.',
      },
      {
        subtitle: 'Trámites por tipo y citas de la semana',
        text: `El panel de trámites por tipo muestra cuántos expedientes se abrieron este mes por tipo de procedimiento, con barras de porcentaje. El panel de citas de la semana muestra en tabla todas las citas del lunes al domingo actual con fecha, título, cliente, responsable y estado — permite ver toda la agenda del despacho sin abrir el módulo de Agenda.`,
        example: 'Ejemplo: Compraventa: 8 (53%) · Poder Notarial: 4 (27%) · Donación: 2 (13%) · Total: 14 trámites este mes.',
      },
      {
        subtitle: 'Carga de trabajo por empleado',
        text: `Tabla con todos los miembros del equipo: expedientes activos, cuántos sin actividad (en rojo si hay), cuántos completados y una barra relativa de carga. Si un empleado tiene expedientes sin actividad su barra aparece en rojo. Al hacer clic en el nombre puedes ver directamente sus expedientes asignados.`,
        example: 'Ejemplo: Ana Torres · Activos: 12 · Sin actividad: ⚠️ 2 · Barra roja 100%. Carlos López · Activos: 6 · Sin actividad: — · Barra azul 50%.',
      },
    ],
  },

  // ─── REFERENCIA: ROLES ───────────────────────────────────────────────────
  {
    id: 'roles',
    icon: '🔐',
    title: 'Roles y permisos',
    step: null,
    roles: ['staff', 'supervisor', 'notario'],
    content: [
      {
        subtitle: 'Staff — Operativo',
        text: `El staff es el personal operativo del despacho. Tiene acceso únicamente a los clientes y expedientes que le han sido asignados directamente. Puede: registrar clientes, crear expedientes, registrar actividades, gestionar el checklist de documentos, subir archivos a Drive, crear presupuestos, registrar pagos y agendar citas. No puede validar pagos, no puede ver notas privadas, y no tiene acceso a Configuración, Cuentas por Cobrar ni Dashboard Ejecutivo.`,
        example: null,
      },
      {
        subtitle: 'Supervisor — Gestión y supervisión',
        text: `El supervisor tiene acceso a todos los clientes y expedientes del despacho, sin importar quién los tenga asignados. Puede hacer todo lo que hace el staff pero con visibilidad completa. Puede ver y crear notas privadas, ver las citas y expedientes de todos los miembros del equipo, y acceder a Cuentas por Cobrar y Dashboard Ejecutivo. No tiene acceso al módulo de Configuración.`,
        example: null,
      },
      {
        subtitle: 'Notario — Acceso completo',
        text: `El notario tiene acceso completo al sistema sin restricciones. Además de todo lo que puede hacer el supervisor, puede: validar pagos, acceder a Configuración para gestionar tipos de documentos, categorías y cuentas bancarias, ver el historial completo de cambios de cualquier expediente, conectar y gestionar la integración con Google Drive, gestionar usuarios (crear, editar, desactivar) y acceder al Log de Auditoría.`,
        example: null,
      },
    ],
  },

  // ─── REFERENCIA: BÚSQUEDA ────────────────────────────────────────────────
  {
    id: 'busqueda',
    icon: '🔍',
    title: 'Búsqueda global',
    step: null,
    roles: ['staff', 'supervisor', 'notario'],
    content: [
      {
        subtitle: '¿Cómo funciona?',
        text: `La barra de búsqueda global se encuentra en la parte superior del sistema y está disponible desde cualquier módulo en todo momento. Permite buscar simultáneamente en clientes, expedientes y presupuestos con solo escribir un nombre o número de folio. Los resultados aparecen agrupados por tipo: Clientes, Expedientes y Presupuestos. Al hacer clic en cualquier resultado vas directamente al detalle. El staff solo verá resultados de sus clientes y expedientes asignados.`,
        example: 'Ejemplo: Escribes "García" y el sistema muestra: Clientes → "Juan García López"; Expedientes → "Compraventa Calle Hidalgo 45 (Juan García)"; Presupuestos → "CV-0023 · Compraventa (Juan García)".',
      },
    ],
  },

  // ─── REFERENCIA: AUDITORÍA ───────────────────────────────────────────────
  {
    id: 'auditoria',
    icon: '📋',
    title: 'Log de Auditoría',
    step: null,
    roles: ['notario'],
    content: [
      {
        subtitle: '¿Qué es el Log de Auditoría?',
        text: `El Log de Auditoría es un registro automático e inmutable de absolutamente todos los cambios realizados en el sistema. Corre en segundo plano sin intervención del usuario — cada vez que alguien crea, edita o elimina un cliente, expediente, presupuesto o cita, el sistema registra automáticamente quién lo hizo, qué campo cambió, cuál era el valor anterior y cuál es el nuevo. En una notaría esto puede ser un requisito legal para demostrar la trazabilidad de los trámites.`,
        example: 'Ejemplo: El notario necesita saber quién cambió el estado de un expediente a "Cancelado". Filtra por módulo "Expedientes" y fecha, y ve: "Notario Principal · Editado · status: en_progress → cancelled · 28 abr 14:32".',
      },
      {
        subtitle: 'Log global y filtros',
        text: `El Log global es accesible desde el menú lateral (solo notario). Muestra todos los cambios de todos los módulos. Cuatro filtros combinables: Módulo (Expedientes, Clientes, Presupuestos, Citas), Usuario, Fecha desde y Fecha hasta. Cada fila muestra la acción (Creado en verde, Editado en azul, Eliminado en rojo), el módulo, el ID del registro, el usuario, el detalle del cambio (valor anterior tachado en rojo y nuevo en verde) y la fecha exacta.`,
        example: 'Ejemplo: El notario filtra por Usuario "Ana Torres" y mes de abril. Ve todas las acciones de Ana: 12 expedientes editados, 3 clientes creados, 5 presupuestos modificados.',
      },
      {
        subtitle: 'Log por expediente, cliente y presupuesto',
        text: `Dentro del detalle de cada expediente hay una pestaña "Auditoría" (solo notario) con los cambios específicos de ese expediente. En el perfil de cada cliente aparece una card de Auditoría al final. En el detalle de cada presupuesto hay también una pestaña "Auditoría". Estos logs contextuales son complementarios al log global — permiten ver el historial de un registro específico sin salir de su página.`,
        example: 'Ejemplo: Pestaña Auditoría del expediente de Juan García muestra: "assigned_user_id: 2 → 3 · Carlos López · 15 abr 14:20" — el expediente fue reasignado ese día.',
      },
    ],
  },

  // ─── REFERENCIA: FAQ ─────────────────────────────────────────────────────
  {
    id: 'faq',
    icon: '❓',
    title: 'Preguntas frecuentes',
    step: null,
    roles: ['staff', 'supervisor', 'notario'],
    content: [
      {
        subtitle: '¿Cómo cambio mi contraseña?',
        text: `Haz clic en el ícono de persona en la esquina superior derecha del sistema y selecciona "Mi perfil". En la sección "Cambiar contraseña" ingresa tu contraseña actual y la nueva contraseña (mínimo 8 caracteres). Haz clic en "Actualizar contraseña". El cambio aplica de inmediato.`,
        example: null,
        callouts: [
          { type: 'tip', text: 'Si olvidaste tu contraseña actual, el notario puede restablecerla desde Configuración → Usuarios.' },
        ],
      },
      {
        subtitle: '¿Por qué no puedo ver los expedientes de mis compañeros?',
        text: `Si tu rol es "Staff", el sistema solo te muestra los expedientes y clientes que te han sido asignados directamente. Esto es por diseño: protege la confidencialidad de los trámites y evita confusiones. Si necesitas acceso a un expediente de otro compañero, el notario o supervisor puede reasignarlo o consultar los datos por ti.`,
        example: null,
        callouts: [
          { type: 'important', text: 'Solo el notario o supervisor puede cambiar el responsable de un expediente.' },
        ],
      },
      {
        subtitle: '¿Qué pasa si borro un cliente?',
        text: `El sistema no permite eliminar clientes de forma permanente. En su lugar puedes "desactivar" al cliente — esto lo oculta de las listas activas pero conserva todo su historial: expedientes, actividades, presupuestos y pagos. Un cliente desactivado puede reactivarse en cualquier momento. Esta decisión de diseño garantiza que el despacho nunca pierda información histórica.`,
        example: null,
        callouts: [
          { type: 'warning', text: 'No existe una opción de borrado permanente de clientes. La desactivación es reversible.' },
        ],
      },
      {
        subtitle: '¿Por qué el botón de "Respaldar ahora" está deshabilitado?',
        text: `El botón de respaldo se habilita solo cuando Google Drive está conectado. Ve a Configuración → Configuración del Sistema → sección "Integraciones" y verifica que aparezca "✓ Conectado". Si no está conectado haz clic en "Conectar Drive" y autoriza el acceso. Si ya estaba conectado pero el botón sigue deshabilitado, usa "Reconectar" para refrescar el token de acceso.`,
        example: null,
        callouts: [
          { type: 'tip', text: 'El estado de conexión con Drive puede expirar si no se usa el sistema por mucho tiempo. Usa el botón "Reconectar" para renovarlo.' },
        ],
      },
      {
        subtitle: '¿Puedo generar el presupuesto antes de que el cliente pague?',
        text: `Sí, absolutamente. El presupuesto puede generarse y enviarse al cliente como PDF en cualquier estado: Borrador, Enviado o Aceptado. Los pagos se registran dentro del presupuesto conforme el cliente va abonando. El sistema muestra en tiempo real el saldo pagado y el saldo pendiente. El estado "Aceptado" indica que el cliente aprobó el presupuesto, no que ya pagó.`,
        example: 'Ejemplo: Generas el presupuesto (Borrador), lo exportas como PDF Elegante y se lo envías al cliente. Cuando el cliente dice que acepta, cambias el estado a "Aceptado". Los pagos los registras cuando efectivamente lleguen.',
      },
      {
        subtitle: '¿Cómo vinculo una cita a un expediente?',
        text: `Al crear una cita en la Agenda, primero selecciona el cliente en el campo "Cliente" — una vez seleccionado aparece el campo "Expediente" donde puedes elegir uno de sus expedientes activos. También puedes crear la cita directamente desde el expediente usando el botón "📅 Agendar cita" en el encabezado del expediente, que pre-llena automáticamente cliente y expediente.`,
        example: null,
        callouts: [
          { type: 'tip', text: 'Si creas la cita desde el expediente, el campo "Expediente" ya viene llenado automáticamente — es la forma más rápida.' },
        ],
      },
      {
        subtitle: '¿El sistema funciona sin Google Drive?',
        text: `Sí. Google Drive es una integración opcional. Sin ella el sistema funciona completamente: puedes gestionar clientes, expedientes, presupuestos, citas y toda la operación del despacho. La única función que requiere Drive es subir archivos al checklist de documentos y generar respaldos de base de datos. Si no configuras Drive simplemente no aparecerán esas opciones.`,
        example: null,
      },
      {
        subtitle: '¿Qué significa el punto naranja en la pestaña "Versiones" del presupuesto?',
        text: `El punto naranja indica que hay cambios en el presupuesto que todavía no se han guardado como versión. Cada vez que editas un concepto, cambias un monto o modificas información del presupuesto, el sistema detecta los cambios y muestra este indicador para recordarte que deberías guardar una versión con una descripción de lo que cambió. No es un error — es un recordatorio visual.`,
        example: null,
        callouts: [
          { type: 'tip', text: 'Guarda una versión antes de hacer cambios grandes. Las versiones anteriores nunca se pierden y puedes restaurar cualquiera.' },
        ],
      },
      {
        subtitle: '¿Con qué frecuencia debo hacer el respaldo de base de datos?',
        text: `Se recomienda hacer el respaldo al menos una vez por semana, o después de cualquier día con mucha actividad (varios expedientes creados, muchos presupuestos modificados, cierres de trámites). El respaldo es manual — el sistema no lo hace automáticamente. Establece un recordatorio semanal para el notario o para quien tenga acceso a Configuración.`,
        example: null,
        callouts: [
          { type: 'warning', text: 'El sistema NO genera respaldos automáticos. Si no los haces manualmente, podrías perder información en caso de falla del servidor.' },
        ],
      },
      {
        subtitle: '¿Puedo tener el mismo correo en dos usuarios?',
        text: `No. El correo electrónico es el identificador único de cada usuario en el sistema. Si intentas crear dos usuarios con el mismo correo, el sistema mostrará un error de validación. Si un empleado cambia de correo, el notario puede editarlo desde Configuración → Usuarios.`,
        example: null,
      },
      {
        subtitle: 'Un expediente aparece sin responsable asignado — ¿qué hago?',
        text: `Esto ocurre cuando se desactiva al usuario que tenía ese expediente asignado. El expediente sigue existiendo pero nadie lo está atendiendo. Para resolverlo: ve al módulo Expedientes, busca los expedientes que muestren "Sin responsable" (o filtra por el usuario desactivado), ábrelos uno por uno y en el campo "Responsable" asigna a la persona correcta. El cambio queda registrado en el historial del expediente. Si son muchos, el notario puede usar el Log de Auditoría para identificar cuándo se desactivó el usuario y cuántos expedientes afectó.`,
        example: 'Ejemplo: Carlos fue desactivado el lunes. El notario filtra en Expedientes por responsable "Carlos López" y ve 6 expedientes sin atender. Los reasigna entre Ana (4) y Luis (2).',
        callouts: [
          { type: 'tip', text: 'Antes de desactivar a un empleado, reasigna sus expedientes activos. Es más fácil hacerlo antes que rastrearlos después.' },
        ],
      },
      {
        subtitle: 'El cliente dice que no recibió el PDF del presupuesto — ¿cómo lo reenvío?',
        text: `El sistema no envía correos automáticamente — el PDF se genera y tú lo descargas para enviarlo manualmente. Para reenviar: abre el presupuesto, haz clic en "Generar PDF" con el estilo que prefieres (Clásico o Elegante), descarga el archivo y envíalo al cliente por correo, WhatsApp o el canal que usen. No existe un contador de envíos — puedes generar el PDF las veces que necesites sin que eso afecte el estado del presupuesto.`,
        example: 'Ejemplo: El cliente dice no haber recibido el presupuesto. Abres CV-0023, generas el PDF Elegante, lo descargas y se lo mandas por WhatsApp. El presupuesto no cambia de estado por esto.',
        callouts: [
          { type: 'tip', text: 'Si quieres dejar constancia de que enviaste el presupuesto, registra una actividad en el expediente: "Tipo: Gestión · Descripción: Presupuesto CV-0023 enviado al cliente por WhatsApp".' },
        ],
      },
      {
        subtitle: 'Subí un documento equivocado al checklist — ¿cómo lo reemplazo?',
        text: `Para reemplazar un archivo del checklist: abre el expediente, ve a la pestaña "Documentos" y localiza el documento equivocado. Haz clic en el ícono de editar o en el nombre del archivo — aparecerá la opción de subir un nuevo archivo. Al subir el archivo correcto, el archivo anterior en Google Drive se reemplaza y el enlace del documento en el checklist se actualiza automáticamente. Si el archivo equivocado ya no debe estar en Drive, el notario puede eliminarlo manualmente desde Google Drive.`,
        example: 'Ejemplo: Subiste el acta de nacimiento del vendedor en lugar del comprador. Editas el documento en el checklist, subes el archivo correcto. El enlace a Drive queda actualizado.',
        callouts: [
          { type: 'important', text: 'Si Drive no está conectado, el checklist solo registra el nombre y estado del documento sin archivos adjuntos. En ese caso simplemente edita el estado del documento.' },
        ],
      },
    ],
  },

  // ─── REFERENCIA: RESPALDO ────────────────────────────────────────────────
  {
    id: 'backup',
    icon: '💾',
    title: 'Respaldo de base de datos',
    step: null,
    roles: ['notario'],
    content: [
      {
        subtitle: '¿Qué es el respaldo?',
        text: `El sistema permite generar manualmente un respaldo completo de la base de datos y subirlo automáticamente a Google Drive. El respaldo incluye toda la información: clientes, expedientes, presupuestos, citas, actividades, pagos y configuraciones. El archivo generado es un dump comprimido (.sql.gz) que puede restaurarse en caso de pérdida de datos.`,
        example: 'Ejemplo: El notario genera un respaldo cada lunes. Si ocurre un problema con el servidor, el equipo técnico puede restaurar la base de datos desde el archivo en Drive.',
      },
      {
        subtitle: '¿Cómo generar el respaldo?',
        text: `Ve a Configuración → Configuración del Sistema y busca la sección "Respaldo de base de datos" al final. Asegúrate de tener Google Drive conectado — si no está conectado el botón aparece deshabilitado. Haz clic en "Respaldar ahora" y espera 5 a 30 segundos. Verás una notificación verde confirmando que el archivo fue subido a Drive, en la carpeta "Lex Manager Backups".`,
        example: 'Ejemplo: El notario hace clic en "Respaldar ahora". Después de unos segundos aparece: "Backup completado exitosamente." El archivo 2026-04-30-00-19-48.sql.gz aparece en su Google Drive.',
        callouts: [
          { type: 'warning', text: 'El sistema NO genera respaldos automáticos. Establece un recordatorio semanal para hacerlo manualmente. Un respaldo reciente puede salvar toda la información del despacho ante una falla.' },
        ],
      },
    ],
  },

  // ─── REFERENCIA: ESCENARIOS FRECUENTES ───────────────────────────────────
  {
    id: 'escenarios',
    icon: '🎬',
    title: 'Escenarios frecuentes',
    step: null,
    roles: ['staff', 'supervisor', 'notario'],
    type: 'scenarios',
    content: [],
    scenarios: [
      {
        id: 'tramite-completo',
        icon: '🏠',
        title: 'Trámite completo de inicio a fin',
        desc: 'Desde que llega el cliente hasta que se cierra el expediente y se cobra.',
        duration: '~8 pasos',
        roles: ['staff', 'supervisor', 'notario'],
        steps: [
          { who: 'staff',      icon: '👥', action: 'Registrar al cliente',             desc: 'Ve a Clientes → Nuevo Cliente. Llena nombre, teléfono, correo y el motivo de la visita.', section: 'clientes',          path: '/clients/create' },
          { who: 'staff',      icon: '📁', action: 'Abrir el expediente',              desc: 'Desde el perfil del cliente usa "Crear expediente". Selecciona tipo de trámite, asígnate como responsable y pon el estado en "En proceso".', section: 'expedientes',       path: '/case-files' },
          { who: 'staff',      icon: '📄', action: 'Solicitar documentos',             desc: 'En la pestaña Documentos del expediente agrega los documentos requeridos. Comparte la lista con el cliente.', section: 'expedientes',       path: null },
          { who: 'staff',      icon: '🏷️', action: 'Registrar actividades',           desc: 'Cada llamada, visita o avance se registra en la pestaña Actividades. Esto mantiene al supervisor informado sin necesidad de reportes.', section: 'expedientes',       path: null },
          { who: 'staff',      icon: '📅', action: 'Agendar la cita de firma',         desc: 'Desde el expediente usa el botón "📅 Agendar cita" — cliente y expediente se pre-llenan solos. Solo elige fecha y hora.', section: 'agenda',            path: '/agenda' },
          { who: 'staff',      icon: '💰', action: 'Generar el presupuesto',           desc: 'En la pestaña Presupuestos del expediente → Nuevo Presupuesto. Selecciona el tipo de documento, ajusta montos y guarda.', section: 'presupuestos',      path: '/budgets/create' },
          { who: 'staff',      icon: '💳', action: 'Registrar el pago al cobrar',      desc: 'Dentro del presupuesto → Registrar pago. Indica monto, fecha y método. Sube el comprobante si Drive está conectado.', section: 'presupuestos',      path: null },
          { who: 'notario',    icon: '✅', action: 'Validar pago y cerrar expediente', desc: 'El notario valida el pago desde el presupuesto y cambia el estado del expediente a "Completado".', section: 'presupuestos',      path: null },
        ],
      },
      {
        id: 'cobrar-presupuesto',
        icon: '💰',
        title: 'Cobrar un presupuesto paso a paso',
        desc: 'Cómo registrar un pago, validarlo y verificar que desaparezca de cuentas por cobrar.',
        duration: '~5 pasos',
        roles: ['staff', 'supervisor', 'notario'],
        steps: [
          { who: 'staff',      icon: '💰', action: 'Abrir el presupuesto',             desc: 'Ve a Presupuestos o búscalo desde el expediente del cliente en la pestaña "Presupuestos".', section: 'presupuestos',      path: '/budgets' },
          { who: 'staff',      icon: '🔄', action: 'Cambiar estado a "Aceptado"',      desc: 'Si el cliente aprobó el presupuesto, cambia el estado de Borrador o Enviado a "Aceptado" antes de registrar pagos.', section: 'presupuestos',      path: null },
          { who: 'staff',      icon: '📝', action: 'Registrar el pago',               desc: 'Haz clic en "Registrar pago". Llena monto, fecha, método (transferencia, cheque o efectivo) y sube el comprobante. El sistema calcula automáticamente el saldo pendiente.', section: 'presupuestos',      path: null },
          { who: 'notario',    icon: '✅', action: 'Validar el pago',                  desc: 'El notario entra al presupuesto y hace clic en "Validar" junto al pago registrado. Solo los pagos validados se reflejan en el Dashboard Ejecutivo y en el total cobrado.', section: 'presupuestos',      path: null },
          { who: 'supervisor', icon: '💳', action: 'Verificar en Cuentas por Cobrar',  desc: 'Ve a Cuentas por Cobrar y confirma que el saldo pendiente de ese presupuesto se redujo. Si el saldo llegó a $0 ya no aparece en la lista.', section: 'cuentas-por-cobrar', path: '/accounts-receivable' },
        ],
      },
      {
        id: 'reasignacion',
        icon: '🔄',
        title: 'Reasignar expedientes de un empleado',
        desc: 'Cuando un empleado sale del despacho o entra uno nuevo y hay que redistribuir la carga.',
        duration: '~4 pasos',
        roles: ['notario'],
        steps: [
          { who: 'notario',    icon: '👤', action: 'Desactivar al empleado',              desc: 'Ve a Configuración → Usuarios, abre el perfil del empleado que deja el despacho y desactiva el toggle "Usuario activo". El empleado pierde acceso de inmediato.', section: 'usuarios', path: '/settings/users' },
          { who: 'notario',    icon: '🔍', action: 'Identificar sus expedientes activos', desc: 'Ve al módulo Expedientes y filtra por el responsable desactivado. El sistema mostrará todos los expedientes que siguen asignados a esa persona y que nadie está atendiendo.', section: 'expedientes', path: '/case-files' },
          { who: 'notario',    icon: '🔄', action: 'Reasignar uno por uno',               desc: 'Abre cada expediente, edita el campo "Responsable" y selecciona a la persona correcta del equipo activo. El cambio queda registrado en el historial del expediente con fecha y hora.', section: 'expedientes', path: null },
          { who: 'supervisor', icon: '📊', action: 'Verificar la nueva distribución',     desc: 'En el Dashboard Ejecutivo → tabla "Carga de trabajo" confirma que los expedientes ya aparecen bajo el nuevo responsable y que nadie quedó con sobrecarga excesiva.', section: 'dashboard-ejecutivo', path: '/notary-dashboard' },
        ],
      },
      {
        id: 'cita-completa',
        icon: '📅',
        title: 'Agendar y completar una cita',
        desc: 'Desde programar la cita hasta que queda registrada como actividad en el expediente.',
        duration: '~5 pasos',
        roles: ['staff', 'supervisor', 'notario'],
        steps: [
          { who: 'staff',      icon: '📅', action: 'Crear la cita',                   desc: 'En la Agenda haz clic en el horario deseado o usa "+ Nueva cita". Selecciona cliente y expediente para vincularla. Elige tipo (Firma, Consulta, Entrega…) y responsable.', section: 'agenda',            path: '/agenda' },
          { who: 'staff',      icon: '✔️', action: 'Confirmar asistencia',            desc: 'Cuando el cliente confirme, abre la cita y haz clic en "Confirmar". La cita cambia de azul a verde en el calendario.', section: 'agenda',            path: null },
          { who: 'staff',      icon: '🏁', action: 'Marcar como realizada',           desc: 'Al terminar la cita, ábrela y haz clic en "Marcar realizada". Escribe las notas de lo que ocurrió (firma completa, documentos entregados, etc.).', section: 'agenda',            path: null },
          { who: 'staff',      icon: '🔗', action: 'Aceptar el registro automático',  desc: 'Si la cita está vinculada a un expediente, el sistema pregunta: "¿Registrar actividad en el expediente?" Acepta — así no tienes que hacerlo manualmente dos veces.', section: 'agenda',            path: null },
          { who: 'supervisor', icon: '📊', action: 'Revisar estadísticas de agenda',  desc: 'En Agenda → Estadísticas puedes ver la tasa de completado del mes, citas por responsable y tendencias. Útil para detectar cancelaciones frecuentes.', section: 'agenda',            path: '/agenda/stats' },
        ],
      },
    ],
  },
];

// ── Diagrama de flujo visual ─────────────────────────────────────────────────
const FlowNode = ({ icon, label, sublabel, color = '#4F63D2', bg = '#EEF0FB', onClick }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
      padding: '12px 14px', borderRadius: 12,
      background: bg, border: `2px solid ${color}22`,
      minWidth: 90, maxWidth: 120, textAlign: 'center',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.15s, box-shadow 0.15s',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      fontFamily: 'sans-serif',
    }}
    onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'; } }}
    onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; }}
  >
    <span style={{ fontSize: 22 }}>{icon}</span>
    <span style={{ fontSize: 12, fontWeight: 700, color, lineHeight: 1.3 }}>{label}</span>
    {sublabel && <span style={{ fontSize: 10, color: '#9CA3AF', lineHeight: 1.3 }}>{sublabel}</span>}
  </div>
);

const Arrow = ({ vertical = false, label }) => (
  <div style={{
    display: 'flex', flexDirection: vertical ? 'column' : 'row',
    alignItems: 'center', justifyContent: 'center',
    gap: 2, flexShrink: 0,
    padding: vertical ? '4px 0' : '0 4px',
  }}>
    {label && (
      <span style={{ fontSize: 9, color: '#9CA3AF', fontFamily: 'sans-serif', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </span>
    )}
    <span style={{ fontSize: vertical ? 16 : 14, color: '#CBD5E1', lineHeight: 1 }}>
      {vertical ? '↓' : '→'}
    </span>
  </div>
);

function FlowDiagram({ onNavigate }) {
  return (
    <div style={{
      background: '#F8FAFF', border: '1px solid #E5E7EB', borderRadius: 14,
      padding: '20px 16px', marginBottom: 20, overflow: 'hidden',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'sans-serif', marginBottom: 16 }}>
        📌 Así se conectan los módulos del sistema
      </div>

      {/* ── Fila principal: Cliente → Expediente ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 4, marginBottom: 16 }}>
        <FlowNode icon="👥" label="Cliente" sublabel="Datos de contacto" color="#1D4ED8" bg="#EFF6FF" onClick={() => onNavigate('clientes')} />
        <Arrow label="registras" />
        <FlowNode icon="📁" label="Expediente" sublabel="Trámite formal" color="#4F63D2" bg="#EEF0FB" onClick={() => onNavigate('expedientes')} />
        <Arrow label="generan" />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
          {/* Rama 1: Documentos */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Arrow />
            <FlowNode icon="📄" label="Checklist" sublabel="Documentos" color="#059669" bg="#F0FDF4" onClick={() => onNavigate('expedientes')} />
          </div>
          {/* Rama 2: Actividades */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Arrow />
            <FlowNode icon="🏷️" label="Actividades" sublabel="Seguimiento" color="#7C3AED" bg="#F5F3FF" onClick={() => onNavigate('expedientes')} />
          </div>
          {/* Rama 3: Citas */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Arrow />
            <FlowNode icon="📅" label="Citas" sublabel="Agenda" color="#0284C7" bg="#F0F9FF" onClick={() => onNavigate('agenda')} />
          </div>
          {/* Rama 4: Presupuesto → Pagos → CxC */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <Arrow />
            <FlowNode icon="💰" label="Presupuesto" sublabel="Honorarios e impuestos" color="#D97706" bg="#FFFBEB" onClick={() => onNavigate('presupuestos')} />
            <Arrow label="registras pagos" />
            <FlowNode icon="💳" label="CxC" sublabel="Por cobrar" color="#DC2626" bg="#FEF2F2" onClick={() => onNavigate('cuentas-por-cobrar')} />
          </div>
        </div>
      </div>

      {/* ── Leyenda ── */}
      <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 12, display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center' }}>
        {[
          { icon: '👥', label: 'Clientes', section: 'clientes', color: '#1D4ED8' },
          { icon: '📁', label: 'Expedientes', section: 'expedientes', color: '#4F63D2' },
          { icon: '📅', label: 'Agenda', section: 'agenda', color: '#0284C7' },
          { icon: '💰', label: 'Presupuestos', section: 'presupuestos', color: '#D97706' },
          { icon: '💳', label: 'Cuentas por Cobrar', section: 'cuentas-por-cobrar', color: '#DC2626' },
        ].map(item => (
          <button
            key={item.section}
            onClick={() => onNavigate(item.section)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 12, fontFamily: 'sans-serif', color: item.color,
              fontWeight: 600, padding: '2px 6px', borderRadius: 6,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            {item.icon} {item.label} →
          </button>
        ))}
      </div>
      <p style={{ margin: '10px 0 0', fontSize: 11, color: '#9CA3AF', textAlign: 'center', fontFamily: 'sans-serif' }}>
        Haz clic en cualquier módulo para ir directamente a esa sección del manual
      </p>
    </div>
  );
}

// ── Vista de escenarios frecuentes ────────────────────────────────────────────
const whoConfig = {
  staff:      { label: 'Staff',      bg: '#E8F4FD', color: '#1565C0' },
  supervisor: { label: 'Supervisor', bg: '#E8F5E9', color: '#2E7D32' },
  notario:    { label: 'Notario',    bg: '#FFF8E1', color: '#B45309' },
};

function ScenariosView({ section, onNavigate, navigateSystem }) {
  const [activeScenario, setActiveScenario] = React.useState(section.scenarios[0].id);
  const scenario = section.scenarios.find(s => s.id === activeScenario);

  return (
    <div>
      {/* Tarjetas de selección */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
        {section.scenarios.map(s => {
          const active = s.id === activeScenario;
          return (
            <button
              key={s.id}
              onClick={() => setActiveScenario(s.id)}
              style={{
                textAlign: 'left', padding: '14px 16px', borderRadius: 12,
                border: `2px solid ${active ? '#4F63D2' : '#E5E7EB'}`,
                background: active ? '#EEF0FB' : '#fff',
                cursor: 'pointer', transition: 'all 0.15s',
                boxShadow: active ? '0 2px 8px rgba(79,99,210,0.15)' : '0 1px 3px rgba(0,0,0,0.04)',
              }}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = '#C7D2FE'; e.currentTarget.style.background = '#F5F6FF'; } }}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.background = '#fff'; } }}
            >
              <div style={{ fontSize: 24, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: active ? '#4F63D2' : '#111827', fontFamily: 'sans-serif', marginBottom: 4, lineHeight: 1.3 }}>
                {s.title}
              </div>
              <div style={{ fontSize: 11, color: '#6B7280', fontFamily: 'sans-serif', lineHeight: 1.4, marginBottom: 8 }}>
                {s.desc}
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: active ? '#4F63D2' : '#9CA3AF', fontFamily: 'sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {s.duration}
              </span>
            </button>
          );
        })}
      </div>

      {/* Paso a paso del escenario activo */}
      {scenario && (
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          {/* Header */}
          <div style={{ padding: '16px 20px', background: '#F9FAFB', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>{scenario.icon}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', fontFamily: 'sans-serif' }}>{scenario.title}</div>
              <div style={{ fontSize: 12, color: '#6B7280', fontFamily: 'sans-serif', marginTop: 2 }}>{scenario.desc}</div>
            </div>
          </div>

          {/* Pasos */}
          <div style={{ padding: '20px' }}>
            {scenario.steps.map((step, i) => {
              const isLast = i === scenario.steps.length - 1;
              const wc = whoConfig[step.who] || whoConfig.staff;
              return (
                <div key={i} style={{ display: 'flex', gap: 16, marginBottom: isLast ? 0 : 20, position: 'relative' }}>
                  {/* Línea vertical conectora */}
                  {!isLast && (
                    <div style={{
                      position: 'absolute', left: 17, top: 38, bottom: -12,
                      width: 2, background: '#F3F4F6', borderRadius: 2,
                    }} />
                  )}

                  {/* Número del paso */}
                  <div style={{
                    flexShrink: 0, width: 36, height: 36, borderRadius: '50%',
                    background: '#1e2a4a', color: '#4F63D2',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: 14, fontFamily: 'sans-serif',
                    boxShadow: '0 0 0 4px #F0F1FF', zIndex: 1,
                  }}>
                    {i + 1}
                  </div>

                  {/* Contenido del paso */}
                  <div style={{ flex: 1, paddingTop: 4, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 5 }}>
                      <span style={{ fontSize: 14 }}>{step.icon}</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#111827', fontFamily: 'sans-serif' }}>
                        {step.action}
                      </span>
                      {/* Badge de rol */}
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                        background: wc.bg, color: wc.color, fontFamily: 'sans-serif',
                        border: `1px solid ${wc.color}33`,
                      }}>
                        {wc.label}
                      </span>
                    </div>
                    <p style={{ margin: '0 0 8px', fontSize: 13, color: '#4B5563', lineHeight: 1.7, fontFamily: 'Georgia, serif' }}>
                      {step.desc}
                    </p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {/* Ir a sección del manual */}
                      {step.section && (
                        <button
                          onClick={() => onNavigate(step.section)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '4px 10px', borderRadius: 6,
                            background: '#F3F4F6', border: '1px solid #E5E7EB',
                            color: '#4B5563', fontSize: 11, fontWeight: 600,
                            fontFamily: 'sans-serif', cursor: 'pointer',
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#EEF0FB'; e.currentTarget.style.borderColor = '#C7D2FE'; e.currentTarget.style.color = '#4F63D2'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = '#F3F4F6'; e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.color = '#4B5563'; }}
                        >
                          📖 Ver en el manual
                        </button>
                      )}
                      {/* Ir directo al sistema */}
                      {step.path && (
                        <button
                          onClick={() => navigateSystem(step.path)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '4px 10px', borderRadius: 6,
                            background: '#4F63D2', border: 'none',
                            color: '#fff', fontSize: 11, fontWeight: 600,
                            fontFamily: 'sans-serif', cursor: 'pointer',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#3d4fb8'}
                          onMouseLeave={e => e.currentTarget.style.background = '#4F63D2'}
                        >
                          Ir al sistema
                          <svg width="9" height="9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ padding: '12px 20px', borderTop: '1px solid #F3F4F6', background: '#FAFAFA', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {Object.entries(whoConfig).map(([key, val]) => (
                <span key={key} style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: val.bg, color: val.color, fontFamily: 'sans-serif' }}>
                  {val.label}
                </span>
              ))}
            </div>
            <span style={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'sans-serif' }}>
              Los badges indican quién ejecuta cada paso
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

const roleColors = {
  staff: { bg: '#E8F4FD', text: '#1565C0', border: '#BBDEFB' },
  supervisor: { bg: '#E8F5E9', text: '#2E7D32', border: '#C8E6C9' },
  notario: { bg: '#FFF8E1', text: '#F57F17', border: '#FFECB3' },
};

const roleLabels = { staff: 'Staff', supervisor: 'Supervisor', notario: 'Notario' };

// ── Tiempo de lectura estimado ────────────────────────────────────────────────
function readingTime(section) {
  const words = section.content.reduce((acc, block) => {
    const tw = (block.text?.split(/\s+/).length || 0);
    const ew = (block.example?.split(/\s+/).length || 0);
    const cw = (block.callouts || []).reduce((a, c) => a + (c.text?.split(/\s+/).length || 0), 0);
    return acc + tw + ew + cw;
  }, 0);
  return Math.max(1, Math.ceil(words / 180)); // 180 palabras/min lectura técnica
}

const STORAGE_KEY = 'lexmanager_manual_visited';

function getVisited() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
}
function saveVisited(ids) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ids)); } catch {}
}

export default function UserManual() {
  const [activeSection, setActiveSection] = useState('introduccion');
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [visited, setVisited] = useState(getVisited);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [qsDone, setQsDone] = useState(getQuickstartDone);
  const [qsCollapsed, setQsCollapsed] = useState(false);
  const { user } = useAuth();
  const contentRef = useRef(null);
  const navigate = useNavigate();

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    }).catch(() => {
      // fallback para navegadores sin clipboard API
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    });
  };

  const userRole = user?.role === 'notary' ? 'notario' : user?.role === 'supervisor' ? 'supervisor' : 'staff';
  const visibleSections = sections.filter(s => s.roles.includes(userRole));

  // Quickstart helpers
  const qsSteps = quickstartByRole[userRole] || [];
  const qsProgress = qsSteps.filter(s => qsDone.includes(s.id)).length;
  const qsAllDone = qsProgress === qsSteps.length;

  const toggleQsStep = (id) => {
    const next = qsDone.includes(id) ? qsDone.filter(x => x !== id) : [...qsDone, id];
    setQsDone(next);
    saveQuickstartDone(next);
  };

  const goToPath = (path) => {
    navigate(path);
  };
  const onboardingSections = visibleSections.filter(s => s.step !== null);
  const referenceSections = visibleSections.filter(s => s.step === null);

  // Marcar sección como visitada al navegar
  const navigateTo = (id, blockIndex = null) => {
    setActiveSection(id);
    setSearchQuery('');
    setMobileMenuOpen(false);

    if (blockIndex !== null) {
      // Expandir el bloque destino y colapsar el resto
      const targetKey = `${id}-${blockIndex}`;
      setExpandedBlocks({ [targetKey]: true });
    } else {
      setExpandedBlocks({});
    }

    if (!visited.includes(id)) {
      const next = [...visited, id];
      setVisited(next);
      saveVisited(next);
    }

    if (blockIndex !== null) {
      // Scroll al bloque específico después del render
      setTimeout(() => {
        const el = document.getElementById(`block-${id}-${blockIndex}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Pulso visual para indicar el bloque destino
          el.style.transition = 'box-shadow 0.3s';
          el.style.boxShadow = '0 0 0 3px rgba(79,99,210,0.4)';
          setTimeout(() => { el.style.boxShadow = ''; }, 1400);
        }
      }, 120);
    } else {
      setTimeout(() => contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    }
  };

  // Marcar primera sección como visitada al cargar
  useEffect(() => {
    if (!visited.includes('introduccion')) {
      const next = [...visited, 'introduccion'];
      setVisited(next);
      saveVisited(next);
    }
  }, []); // eslint-disable-line

  const current = visibleSections.find(s => s.id === activeSection);

  // Expandir/colapsar todos
  const allExpanded = current?.content.every((_, i) => expandedBlocks[`${activeSection}-${i}`] !== false);
  const toggleAll = () => {
    if (!current) return;
    const next = {};
    current.content.forEach((_, i) => { next[`${activeSection}-${i}`] = !allExpanded; });
    setExpandedBlocks(next);
  };

  const toggleBlock = (key) => {
    setExpandedBlocks(prev => ({ ...prev, [key]: prev[key] === false ? true : false }));
  };

  const isExpanded = (key) => expandedBlocks[key] !== false;

  // Búsqueda — incluye índice del bloque para deep link
  const searchResults = searchQuery.trim().length >= 2
    ? visibleSections.flatMap(section =>
        section.content
          .map((block, blockIndex) => ({ section, block, blockIndex }))
          .filter(({ block }) =>
            block.subtitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
            block.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (block.example || '').toLowerCase().includes(searchQuery.toLowerCase())
          )
      )
    : [];

  const highlight = (text) => {
    if (!searchQuery.trim()) return text;
    const parts = text.split(new RegExp(`(${searchQuery.trim()})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === searchQuery.toLowerCase()
        ? <mark key={i} style={{ background: '#FEF08A', color: '#1e2a4a', borderRadius: 2, padding: '0 2px' }}>{part}</mark>
        : part
    );
  };

  const currentIndex = visibleSections.findIndex(s => s.id === activeSection);
  const progress = Math.round((visited.length / visibleSections.length) * 100);

  return (
    <div style={{ fontFamily: 'Georgia, serif', background: '#F7F6F3', minHeight: '100vh', padding: '24px 0' }}>
      <style>{`
        .manual-nav-btn {
          display: flex; align-items: center; gap: 10px;
          width: 100%; padding: 9px 12px;
          border: none; border-radius: 8px;
          background: transparent; cursor: pointer;
          font-size: 13px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: #555; text-align: left;
          transition: background 0.15s, color 0.15s;
          margin-bottom: 2px; position: relative;
        }
        .manual-nav-btn:hover { background: #EEF0FB; color: #1e2a4a; }
        .manual-nav-btn.active { background: #1e2a4a; color: #fff; font-weight: 600; }
        .manual-nav-btn .icon { font-size: 15px; width: 20px; text-align: center; flex-shrink: 0; }
        .manual-nav-btn .visited-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: #22C55E; flex-shrink: 0; margin-left: auto;
        }
        .manual-nav-btn.active .visited-dot { background: rgba(255,255,255,0.5); }
        .content-block {
          border: 1px solid #E8E6E0; border-radius: 10px;
          margin-bottom: 14px; background: #fff; overflow: hidden;
          transition: box-shadow 0.15s;
        }
        .content-block:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.06); }
        .block-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px 18px; cursor: pointer; user-select: none;
        }
        .block-header h6 {
          margin: 0; font-size: 14.5px; font-weight: 700; color: #1e2a4a;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          display: flex; align-items: center; gap: 8px;
        }
        .block-body { padding: 0 18px 18px; border-top: 1px solid #F0EEE8; }
        .block-text {
          font-size: 14px; line-height: 1.85; color: #444;
          margin: 12px 0 10px; font-family: Georgia, serif;
        }
        .example-box {
          background: #F0F7FF; border-left: 3px solid #3B82F6;
          border-radius: 0 6px 6px 0; padding: 10px 14px;
          font-size: 13px; color: #1E40AF;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          line-height: 1.7;
        }
        .example-label {
          font-size: 10.5px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.8px; color: #3B82F6; margin-bottom: 5px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        /* Callouts */
        .callout {
          display: flex; align-items: flex-start; gap: 10px;
          border-radius: 0 8px 8px 0; padding: 10px 14px;
          margin: 10px 0; font-size: 13px; line-height: 1.6;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        .callout-tip      { background: #EFF6FF; border-left: 3px solid #4F63D2; color: #1e40af; }
        .callout-important { background: #FEF3C7; border-left: 3px solid #F59E0B; color: #92400E; }
        .callout-warning  { background: #FEF2F2; border-left: 3px solid #EF4444; color: #991B1B; }
        .callout-icon { font-size: 15px; flex-shrink: 0; margin-top: 1px; }
        .callout-label {
          font-size: 10px; font-weight: 800; text-transform: uppercase;
          letter-spacing: 0.8px; margin-bottom: 3px;
        }
        /* Bloque de código copiable */
        .code-block {
          margin: 10px 0;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        .code-block-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 6px 12px;
          background: #F9FAFB; border-bottom: 1px solid #E5E7EB;
          font-size: 10px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.7px; color: #9CA3AF;
        }
        .code-block-copy {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 3px 10px; border-radius: 6px; border: none;
          background: #EEF0FB; color: #4F63D2; font-size: 11px;
          font-weight: 600; cursor: pointer; transition: background 0.15s;
          font-family: inherit;
        }
        .code-block-copy:hover { background: #dde1f7; }
        .code-block-copy.copied { background: #D1FAE5; color: #065F46; }
        .code-block-value {
          padding: 10px 14px;
          font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
          font-size: 13px; color: #1e2a4a; background: #fff;
          word-break: break-all; line-height: 1.6;
        }
        /* Tiempo de lectura */
        .read-time {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 11px; color: #9CA3AF; font-family: sans-serif;
          background: #F3F4F6; padding: 2px 8px; border-radius: 20px;
          margin-left: 8px; vertical-align: middle;
        }
        /* Botón marcar como leída */
        .mark-read-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 18px; border-radius: 8px; border: 1.5px solid #E5E7EB;
          background: #fff; color: #374151; font-size: 13px; font-weight: 600;
          cursor: pointer; font-family: sans-serif;
          transition: all 0.15s;
        }
        .mark-read-btn:hover { background: #EEF0FB; border-color: #4F63D2; color: #4F63D2; }
        .mark-read-btn.done { background: #D1FAE5; border-color: #A7F3D0; color: #065F46; }
        /* Botón imprimir */
        .print-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 18px; border-radius: 8px; border: 1.5px solid #E5E7EB;
          background: #fff; color: #374151; font-size: 13px; font-weight: 600;
          cursor: pointer; font-family: sans-serif; transition: background 0.15s;
        }
        .print-btn:hover { background: #F9FAFB; }
        @media print {
          .manual-sidebar, .mobile-section-select, .mark-read-btn,
          .print-btn, input[type="text"] { display: none !important; }
          .manual-content-area { min-width: 100% !important; }
          .content-block { break-inside: avoid; }
        }
        .role-badge {
          display: inline-flex; align-items: center;
          padding: 3px 9px; border-radius: 20px;
          font-size: 11px; font-weight: 600;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          margin-right: 5px; border: 1px solid;
        }
        .chevron { transition: transform 0.2s; color: #999; font-size: 11px; }
        .chevron.open { transform: rotate(180deg); }
        .section-header {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 18px; padding-bottom: 14px;
          border-bottom: 2px solid #1e2a4a; flex-wrap: wrap; gap: 10px;
        }
        .section-title {
          font-size: 20px; font-weight: 700; color: #1e2a4a;
          margin: 0; font-family: Georgia, serif;
          display: flex; align-items: center; gap: 10px;
        }
        .search-result-card {
          background: #fff; border: 1px solid #E8E6E0; border-radius: 10px;
          padding: 14px 18px; margin-bottom: 12px; cursor: pointer;
          transition: box-shadow 0.15s, border-color 0.15s;
        }
        .search-result-card:hover {
          box-shadow: 0 2px 12px rgba(0,0,0,0.08);
          border-color: #4F63D2;
        }
        .nav-group-label {
          font-size: 10px; font-weight: 700; letter-spacing: 1.2px;
          text-transform: uppercase; color: #999;
          font-family: sans-serif; padding: 6px 8px 4px;
          border-bottom: 1px solid #F0EEE8; margin-bottom: 6px;
        }
        /* Mobile dropdown */
        .mobile-section-select {
          display: none;
          width: 100%; padding: 10px 12px;
          font-size: 14px; border: 1px solid #E8E6E0;
          border-radius: 8px; background: #fff;
          font-family: sans-serif; color: #1e2a4a;
          margin-bottom: 16px; cursor: pointer;
          appearance: auto;
        }
        @media (max-width: 768px) {
          .manual-sidebar { display: none !important; }
          .mobile-section-select { display: block; }
          .manual-content-area { min-width: 0 !important; }
        }
      `}</style>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 16px' }}>

        {/* Header */}
        <div style={{
          background: '#1e2a4a', borderRadius: 14, padding: '24px 28px',
          marginBottom: 20, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
        }}>
          <div>
            <div style={{ fontSize: 11, color: '#93C5FD', fontFamily: 'sans-serif', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>
              Lex Manager
            </div>
            <h1 style={{ margin: 0, color: '#fff', fontSize: 22, fontFamily: 'Georgia, serif' }}>Manual de Usuario</h1>
            <p style={{ margin: '4px 0 0', color: '#9CA3AF', fontSize: 13, fontFamily: 'sans-serif' }}>
              Guía completa del sistema notarial
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 180 }}>
            {/* Barra de progreso */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.15)', borderRadius: 4, height: 6 }}>
                <div style={{
                  width: `${progress}%`, background: '#4F63D2',
                  height: 6, borderRadius: 4, transition: 'width 0.4s',
                }} />
              </div>
              <span style={{ fontSize: 12, color: '#93C5FD', fontFamily: 'sans-serif', fontWeight: 700, whiteSpace: 'nowrap' }}>
                {progress}%
              </span>
            </div>
            <div style={{ fontSize: 11, color: '#6B7280', fontFamily: 'sans-serif', textAlign: 'right' }}>
              {visited.length} de {visibleSections.length} secciones leídas
            </div>
            {/* Barra de búsqueda */}
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Buscar en el manual..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  width: '100%', padding: '7px 10px 7px 30px',
                  background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 8, color: '#fff', fontSize: 12,
                  fontFamily: 'sans-serif', outline: 'none', boxSizing: 'border-box',
                }}
              />
              <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 13, opacity: 0.6 }}>🔍</span>
              {searchQuery && (
                <span
                  onClick={() => setSearchQuery('')}
                  style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: 14 }}
                >✕</span>
              )}
            </div>
          </div>
        </div>

        {/* ── QUICKSTART PANEL ── */}
        {qsSteps.length > 0 && (
          <div style={{
            background: qsAllDone ? '#F0FDF4' : '#fff',
            borderRadius: 12,
            border: `1px solid ${qsAllDone ? '#A7F3D0' : '#E5E7EB'}`,
            marginBottom: 20,
            overflow: 'hidden',
            transition: 'border-color 0.3s',
          }}>
            {/* Header del panel */}
            <div
              onClick={() => setQsCollapsed(v => !v)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 20px', cursor: 'pointer',
                background: qsAllDone ? '#DCFCE7' : '#F9FAFB',
                borderBottom: qsCollapsed ? 'none' : `1px solid ${qsAllDone ? '#A7F3D0' : '#F3F4F6'}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 20 }}>{qsAllDone ? '🎉' : '🚀'}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: qsAllDone ? '#065F46' : '#111827', fontFamily: 'sans-serif' }}>
                    {qsAllDone ? '¡Sistema listo para usar!' : roleTitle[userRole]}
                  </div>
                  <div style={{ fontSize: 12, color: qsAllDone ? '#059669' : '#6B7280', fontFamily: 'sans-serif', marginTop: 2 }}>
                    {qsAllDone
                      ? 'Completaste todos los pasos de configuración inicial.'
                      : `${qsProgress} de ${qsSteps.length} pasos completados`}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Barra de progreso */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                  <div style={{ flex: 1, background: qsAllDone ? '#A7F3D0' : '#E5E7EB', borderRadius: 4, height: 6 }}>
                    <div style={{
                      width: `${qsSteps.length ? (qsProgress / qsSteps.length) * 100 : 0}%`,
                      background: qsAllDone ? '#059669' : '#4F63D2',
                      height: 6, borderRadius: 4, transition: 'width 0.4s',
                    }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: qsAllDone ? '#059669' : '#4F63D2', fontFamily: 'sans-serif', minWidth: 28 }}>
                    {qsSteps.length ? Math.round((qsProgress / qsSteps.length) * 100) : 0}%
                  </span>
                </div>
                <span style={{ fontSize: 12, color: '#9CA3AF', fontFamily: 'sans-serif' }}>
                  {qsCollapsed ? '▼ Ver' : '▲ Ocultar'}
                </span>
              </div>
            </div>

            {/* Pasos */}
            {!qsCollapsed && (
              <div style={{ padding: '16px 20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
                  {qsSteps.map((step, i) => {
                    const done = qsDone.includes(step.id);
                    return (
                      <div
                        key={step.id}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 12,
                          padding: '12px 14px', borderRadius: 10,
                          border: `1.5px solid ${done ? '#A7F3D0' : '#E5E7EB'}`,
                          background: done ? '#F0FDF4' : '#FAFAFA',
                          transition: 'all 0.2s',
                        }}
                      >
                        {/* Checkbox */}
                        <button
                          onClick={() => toggleQsStep(step.id)}
                          title={done ? 'Marcar como pendiente' : 'Marcar como completado'}
                          style={{
                            flexShrink: 0, width: 22, height: 22, borderRadius: 6,
                            border: `2px solid ${done ? '#059669' : '#D1D5DB'}`,
                            background: done ? '#059669' : '#fff',
                            cursor: 'pointer', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', marginTop: 1,
                            transition: 'all 0.15s',
                          }}
                        >
                          {done && <span style={{ color: '#fff', fontSize: 13, lineHeight: 1 }}>✓</span>}
                        </button>

                        {/* Contenido */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              width: 18, height: 18, borderRadius: 4, fontSize: 9, fontWeight: 800,
                              background: done ? '#A7F3D0' : '#EEF0FB', color: done ? '#065F46' : '#4F63D2',
                              fontFamily: 'sans-serif', flexShrink: 0,
                            }}>{i + 1}</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: done ? '#065F46' : '#111827', fontFamily: 'sans-serif', textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.7 : 1 }}>
                              {step.icon} {step.label}
                            </span>
                          </div>
                          <div style={{ fontSize: 11.5, color: '#6B7280', fontFamily: 'sans-serif', marginBottom: step.time ? 4 : 8, paddingLeft: 24 }}>
                            {step.desc}
                          </div>
                          {step.time && (
                            <div style={{ fontSize: 10.5, color: '#9CA3AF', fontFamily: 'sans-serif', marginBottom: 8, paddingLeft: 24, display: 'flex', alignItems: 'center', gap: 3 }}>
                              ⏱ {step.time}
                            </div>
                          )}
                          {/* Botón "Ir ahora" */}
                          {!done && (
                            <button
                              onClick={() => goToPath(step.path)}
                              style={{
                                marginLeft: 24, display: 'inline-flex', alignItems: 'center', gap: 5,
                                padding: '5px 12px', borderRadius: 7,
                                background: '#4F63D2', color: '#fff',
                                border: 'none', cursor: 'pointer',
                                fontSize: 12, fontWeight: 600, fontFamily: 'sans-serif',
                                transition: 'background 0.15s',
                              }}
                              onMouseEnter={e => e.currentTarget.style.background = '#3d4fb8'}
                              onMouseLeave={e => e.currentTarget.style.background = '#4F63D2'}
                            >
                              Ir ahora
                              <svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Nota al pie */}
                {!qsAllDone && (
                  <p style={{ margin: '14px 0 0', fontSize: 12, color: '#9CA3AF', fontFamily: 'sans-serif' }}>
                    💡 Marca cada paso al completarlo. El progreso se guarda automáticamente en este navegador.
                  </p>
                )}
                {qsAllDone && (
                  <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: '#059669', fontFamily: 'sans-serif', fontWeight: 600 }}>
                      ✓ Configuración completa — ahora explora el manual para sacarle el máximo al sistema.
                    </span>
                    <button
                      onClick={() => { saveQuickstartDone([]); setQsDone([]); }}
                      style={{ fontSize: 11, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'sans-serif' }}
                    >
                      Reiniciar checklist
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Mobile: select de secciones */}
        <select
          className="mobile-section-select"
          value={activeSection}
          onChange={e => navigateTo(e.target.value)}
        >
          <optgroup label="Guía de configuración (en orden)">
            {onboardingSections.map(s => (
              <option key={s.id} value={s.id}>
                {s.step}. {s.title} {visited.includes(s.id) ? '✓' : ''}
              </option>
            ))}
          </optgroup>
          {referenceSections.length > 0 && (
            <optgroup label="Referencia">
              {referenceSections.map(s => (
                <option key={s.id} value={s.id}>
                  {s.title} {visited.includes(s.id) ? '✓' : ''}
                </option>
              ))}
            </optgroup>
          )}
        </select>

        <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>

          {/* Sidebar (desktop) */}
          <div className="manual-sidebar" style={{ width: 210, flexShrink: 0, position: 'sticky', top: 20, alignSelf: 'flex-start' }}>

            {/* ── Panel "Mi rol" ── */}
            {(() => {
              const rolePanelConfig = {
                notario: {
                  color: '#B45309', bg: '#FFFBEB', border: '#FDE68A',
                  icon: '⚖️',
                  tagline: 'Acceso completo al sistema',
                  canDo: [
                    'Configurar toda la notaría',
                    'Validar pagos y auditoría',
                    'Ver métricas ejecutivas',
                    'Gestionar usuarios y Drive',
                  ],
                  cantDo: null,
                  startWith: [
                    { label: 'Configuración inicial', id: 'configuracion' },
                    { label: 'Dashboard Ejecutivo',   id: 'dashboard-ejecutivo' },
                    { label: 'Log de Auditoría',      id: 'auditoria' },
                  ],
                },
                supervisor: {
                  color: '#2E7D32', bg: '#F0FDF4', border: '#A7F3D0',
                  icon: '🔭',
                  tagline: 'Visibilidad total del despacho',
                  canDo: [
                    'Ver todos los expedientes',
                    'Leer notas privadas',
                    'Revisar cuentas por cobrar',
                    'Acceder al Dashboard Ejecutivo',
                  ],
                  cantDo: ['Configuración del sistema', 'Gestionar usuarios', 'Validar pagos'],
                  startWith: [
                    { label: 'Dashboard Ejecutivo',    id: 'dashboard-ejecutivo' },
                    { label: 'Cuentas por Cobrar',     id: 'cuentas-por-cobrar' },
                    { label: 'Escenarios frecuentes',  id: 'escenarios' },
                  ],
                },
                staff: {
                  color: '#1565C0', bg: '#EFF6FF', border: '#BFDBFE',
                  icon: '💼',
                  tagline: 'Tus clientes y expedientes',
                  canDo: [
                    'Registrar clientes',
                    'Abrir y gestionar expedientes',
                    'Agendar citas',
                    'Crear presupuestos y pagos',
                  ],
                  cantDo: ['Configuración', 'Cuentas por Cobrar', 'Dashboard Ejecutivo', 'Notas privadas', 'Validar pagos'],
                  startWith: [
                    { label: 'Primeros pasos',        id: 'clientes' },
                    { label: 'Expedientes',            id: 'expedientes' },
                    { label: 'Escenarios frecuentes', id: 'escenarios' },
                  ],
                },
              };
              const rp = rolePanelConfig[userRole];
              if (!rp) return null;
              return (
                <div style={{
                  background: rp.bg, border: `1px solid ${rp.border}`,
                  borderRadius: 12, padding: '12px 12px 10px',
                  marginBottom: 10,
                }}>
                  {/* Header del rol */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
                    <span style={{ fontSize: 18 }}>{rp.icon}</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: rp.color, fontFamily: 'sans-serif' }}>
                        {roleLabels[userRole]}
                      </div>
                      <div style={{ fontSize: 10, color: rp.color, fontFamily: 'sans-serif', opacity: 0.8 }}>
                        {rp.tagline}
                      </div>
                    </div>
                  </div>

                  {/* Puedes hacer */}
                  <div style={{ fontSize: 10, fontWeight: 700, color: rp.color, textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'sans-serif', marginBottom: 5, opacity: 0.7 }}>
                    Puedes hacer
                  </div>
                  <ul style={{ margin: '0 0 10px', padding: '0 0 0 14px', listStyle: 'disc' }}>
                    {rp.canDo.map((item, i) => (
                      <li key={i} style={{ fontSize: 11, color: rp.color, fontFamily: 'sans-serif', lineHeight: 1.6, opacity: 0.85 }}>
                        {item}
                      </li>
                    ))}
                  </ul>

                  {/* No tienes acceso a */}
                  {rp.cantDo && (
                    <>
                      <div style={{ fontSize: 10, fontWeight: 700, color: rp.color, textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'sans-serif', marginBottom: 5, opacity: 0.7 }}>
                        Sin acceso a
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                        {rp.cantDo.map((item, i) => (
                          <span key={i} style={{
                            fontSize: 10, padding: '2px 7px', borderRadius: 20,
                            background: 'rgba(0,0,0,0.06)', color: rp.color,
                            fontFamily: 'sans-serif', opacity: 0.65,
                            textDecoration: 'line-through',
                          }}>
                            {item}
                          </span>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Empieza por */}
                  <div style={{ fontSize: 10, fontWeight: 700, color: rp.color, textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'sans-serif', marginBottom: 6, opacity: 0.7 }}>
                    Empieza por leer
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {rp.startWith.map((item, i) => (
                      <button
                        key={i}
                        onClick={() => navigateTo(item.id)}
                        style={{
                          textAlign: 'left', background: 'rgba(255,255,255,0.6)',
                          border: `1px solid ${rp.border}`, borderRadius: 6,
                          padding: '4px 8px', cursor: 'pointer',
                          fontSize: 11, fontWeight: 600, color: rp.color,
                          fontFamily: 'sans-serif', transition: 'background 0.15s',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.9)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.6)'}
                      >
                        {item.label}
                        <span style={{ opacity: 0.5, fontSize: 10 }}>→</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div style={{
              background: '#fff', borderRadius: 12, border: '1px solid #E8E6E0',
              padding: '10px 8px',
            }}>
              {/* Configuración en orden */}
              <div className="nav-group-label">Guía de configuración</div>
              {onboardingSections.map(section => (
                <button
                  key={section.id}
                  className={`manual-nav-btn ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => navigateTo(section.id)}
                >
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 18, height: 18, background: activeSection === section.id ? 'rgba(255,255,255,0.2)' : '#EEF0FB',
                    color: activeSection === section.id ? '#fff' : '#4F63D2',
                    borderRadius: 4, fontSize: 10, fontWeight: 700, flexShrink: 0,
                    fontFamily: 'sans-serif',
                  }}>
                    {section.step}
                  </span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{section.title}</span>
                  {visited.includes(section.id) && <span className="visited-dot" />}
                </button>
              ))}

              {/* Referencia */}
              {referenceSections.length > 0 && (
                <>
                  <div className="nav-group-label" style={{ marginTop: 10 }}>Referencia</div>
                  {referenceSections.map(section => (
                    <button
                      key={section.id}
                      className={`manual-nav-btn ${activeSection === section.id ? 'active' : ''}`}
                      onClick={() => navigateTo(section.id)}
                    >
                      <span className="icon">{section.icon}</span>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{section.title}</span>
                      {visited.includes(section.id) && <span className="visited-dot" />}
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Content */}
          <div ref={contentRef} className="manual-content-area" style={{ flex: 1, minWidth: 0 }}>

            {/* ── RESULTADOS DE BÚSQUEDA ── */}
            {searchQuery.trim().length >= 2 ? (
              <div>
                <div style={{ marginBottom: 16, fontFamily: 'sans-serif' }}>
                  <span style={{ fontSize: 13, color: '#6B7280' }}>
                    {searchResults.length === 0
                      ? 'Sin resultados para '
                      : `${searchResults.length} resultado${searchResults.length !== 1 ? 's' : ''} para `}
                  </span>
                  <span style={{ fontSize: 13, color: '#1e2a4a', fontWeight: 700 }}>"{searchQuery}"</span>
                </div>
                {searchResults.length === 0 && (
                  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E8E6E0', padding: '32px', textAlign: 'center', color: '#9CA3AF', fontFamily: 'sans-serif', fontSize: 14 }}>
                    Intenta con otra palabra clave
                  </div>
                )}
                {searchResults.map(({ section, block, blockIndex }, i) => (
                  <div
                    key={i}
                    className="search-result-card"
                    onClick={() => navigateTo(section.id, blockIndex)}
                  >
                    {/* Ruta: Sección › Bloque */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14 }}>{section.icon}</span>
                      <span style={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'sans-serif', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {section.title}
                      </span>
                      <span style={{ fontSize: 11, color: '#CBD5E1' }}>›</span>
                      <span style={{ fontSize: 11, color: '#4F63D2', fontFamily: 'sans-serif', fontWeight: 600 }}>
                        {block.subtitle}
                      </span>
                      {/* Chip "bloque exacto" */}
                      <span style={{
                        marginLeft: 'auto', fontSize: 10, fontWeight: 700,
                        padding: '2px 8px', borderRadius: 20,
                        background: '#EEF0FB', color: '#4F63D2',
                        fontFamily: 'sans-serif', whiteSpace: 'nowrap',
                      }}>
                        ⌖ Ir al bloque exacto
                      </span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1e2a4a', fontFamily: 'sans-serif', marginBottom: 6 }}>
                      {highlight(block.subtitle)}
                    </div>
                    <div style={{ fontSize: 13, color: '#555', fontFamily: 'Georgia, serif', lineHeight: 1.7 }}>
                      {highlight(block.text.substring(0, 200))}{block.text.length > 200 ? '…' : ''}
                    </div>
                  </div>
                ))}
              </div>
            ) : (

            /* ── VISTA NORMAL DE SECCIÓN ── */
            current && (
              <div>
                <div className="section-header">
                  <div>
                    <h2 className="section-title">
                      <span style={{ fontSize: 22 }}>{current.icon}</span>
                      {current.step && (
                        <span style={{
                          fontSize: 11, fontWeight: 700, background: '#1e2a4a', color: '#4F63D2',
                          padding: '2px 8px', borderRadius: 20, fontFamily: 'sans-serif',
                          letterSpacing: 0.5,
                        }}>
                          Paso {current.step}
                        </span>
                      )}
                      {current.title}
                      <span className="read-time">
                        ⏱ ~{readingTime(current)} min
                      </span>
                    </h2>
                    <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {current.roles.map(role => (
                        <span key={role} className="role-badge" style={{ background: roleColors[role].bg, color: roleColors[role].text, borderColor: roleColors[role].border }}>
                          {roleLabels[role]}
                        </span>
                      ))}
                    </div>
                  </div>
                  {/* Expandir / Colapsar todos */}
                  <button
                    onClick={toggleAll}
                    style={{
                      padding: '7px 14px', background: '#F7F6F3', border: '1px solid #E8E6E0',
                      borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontFamily: 'sans-serif',
                      color: '#555', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
                    }}
                  >
                    {allExpanded ? '⊟ Colapsar todo' : '⊞ Expandir todo'}
                  </button>
                </div>

                {/* Diagrama de flujo — solo en la sección de Introducción */}
                {current.id === 'introduccion' && (
                  <FlowDiagram onNavigate={navigateTo} />
                )}

                {/* Vista de escenarios frecuentes — render especial */}
                {current.type === 'scenarios' && (
                  <ScenariosView
                    section={current}
                    onNavigate={navigateTo}
                    navigateSystem={goToPath}
                  />
                )}

                {current.content.map((block, i) => {
                  const key = `${current.id}-${i}`;
                  return (
                    <div key={i} id={`block-${current.id}-${i}`} className="content-block">
                      <div className="block-header" onClick={() => toggleBlock(key)}>
                        <h6>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 22, height: 22, background: '#1e2a4a', color: '#4F63D2',
                            borderRadius: 6, fontSize: 11, fontWeight: 700, flexShrink: 0,
                          }}>
                            {i + 1}
                          </span>
                          {block.subtitle}
                        </h6>
                        <span className={`chevron ${isExpanded(key) ? 'open' : ''}`}>▼</span>
                      </div>
                      {isExpanded(key) && (
                        <div className="block-body">
                          <p className="block-text">{block.text}</p>
                          {/* Glosario */}
                          {block.glossary && (
                            <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden', margin: '10px 0' }}>
                              {block.glossary.map((item, gi) => (
                                <div key={gi} style={{
                                  display: 'flex', gap: 16, padding: '10px 14px',
                                  borderBottom: gi < block.glossary.length - 1 ? '1px solid #F3F4F6' : 'none',
                                  background: gi % 2 === 0 ? '#FAFAFA' : '#fff',
                                  alignItems: 'flex-start',
                                }}>
                                  <span style={{ fontWeight: 700, color: '#1e2a4a', minWidth: 160, fontSize: 12.5, fontFamily: 'sans-serif', flexShrink: 0, paddingTop: 1 }}>
                                    {item.term}
                                  </span>
                                  <span style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.65, fontFamily: 'Georgia, serif' }}>
                                    {item.def}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Bloque de código copiable */}
                          {block.code && (
                            <div className="code-block">
                              <div className="code-block-header">
                                <span>{block.code.label}</span>
                                <button
                                  className={`code-block-copy ${copiedKey === `${key}-code` ? 'copied' : ''}`}
                                  onClick={() => copyToClipboard(block.code.value, `${key}-code`)}
                                >
                                  {copiedKey === `${key}-code` ? '✓ Copiado' : '⎘ Copiar'}
                                </button>
                              </div>
                              <div className="code-block-value">{block.code.value}</div>
                            </div>
                          )}
                          {/* Callouts */}
                          {block.callouts?.map((c, ci) => {
                            const calloutConfig = {
                              tip:       { icon: '💡', label: 'Consejo' },
                              important: { icon: '⚠️', label: 'Importante' },
                              warning:   { icon: '🚫', label: 'Advertencia' },
                            }[c.type] || { icon: 'ℹ️', label: 'Nota' };
                            return (
                              <div key={ci} className={`callout callout-${c.type}`}>
                                <span className="callout-icon">{calloutConfig.icon}</span>
                                <div>
                                  <div className="callout-label">{calloutConfig.label}</div>
                                  {c.text}
                                </div>
                              </div>
                            );
                          })}
                          {block.example && (
                            <div>
                              <div className="example-label">📌 Ejemplo práctico</div>
                              <div className="example-box">{block.example}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Botones: Marcar leída + Imprimir */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 20, paddingTop: 16, borderTop: '1px solid #F0EEE8', flexWrap: 'wrap' }}>
                  <button
                    className={`mark-read-btn ${visited.includes(current.id) ? 'done' : ''}`}
                    onClick={() => {
                      if (!visited.includes(current.id)) {
                        const next = [...visited, current.id];
                        setVisited(next);
                        saveVisited(next);
                      }
                    }}
                  >
                    {visited.includes(current.id) ? '✓ Sección leída' : '○ Marcar como leída'}
                  </button>
                  <button
                    className="print-btn"
                    onClick={() => window.print()}
                    title="Imprimir o guardar como PDF"
                  >
                    🖨 Imprimir sección
                  </button>
                </div>

                {/* Navegación entre secciones */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, gap: 10 }}>
                  {currentIndex > 0 ? (
                    <button
                      onClick={() => navigateTo(visibleSections[currentIndex - 1].id)}
                      style={{
                        padding: '9px 16px', background: '#fff', border: '1px solid #E8E6E0',
                        borderRadius: 8, cursor: 'pointer', fontSize: 13, fontFamily: 'sans-serif',
                        color: '#555', display: 'flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      ← {visibleSections[currentIndex - 1].title}
                    </button>
                  ) : <div />}
                  {currentIndex < visibleSections.length - 1 && (
                    <button
                      onClick={() => navigateTo(visibleSections[currentIndex + 1].id)}
                      style={{
                        padding: '9px 16px', background: '#1e2a4a', border: 'none',
                        borderRadius: 8, cursor: 'pointer', fontSize: 13, fontFamily: 'sans-serif',
                        color: '#fff', display: 'flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      {visibleSections[currentIndex + 1].title} →
                    </button>
                  )}
                </div>
              </div>
            )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
